'use client';

import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import {
  BarChart2,
  ClipboardList,
  CloudUpload,
  Key,
  LayoutList,
  LayoutTemplate,
  MessageSquare,
  PenLine,
  Users,
  Wand2,
} from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeTaskRef } from '../../../db/utils/task-ref.js';
import { addGrammarErrorMutation } from '../../../db/api/add-grammar-error.js';
import { clearPendingCanvasOpenMutation } from '../../../db/api/clear-pending-canvas-open.js';
import { clearPendingDownloadMutation } from '../../../db/api/clear-pending-download.js';
import { createExamMutation } from '../../../db/api/create-exam.js';
import { createExamTasksMutation } from '../../../db/api/create-examTask.js';
import { createSubmissionMutation } from '../../../db/api/create-submission.js';
import { deleteGrammarErrorMutation } from '../../../db/api/delete-grammar-error.js';
import { deleteSubmissionMutation } from '../../../db/api/delete-submission.js';
import { startExamWatcherMutation, stopExamWatcherMutation } from '../../../db/api/exam-watcher.js';
import { updateSubmissionsFoundMutation } from '../../../db/api/update-exam-state.js';
import { generateAnswer } from '../../../db/api/generate-answer.js';
import {
  generateGradebookCsvMutation,
  generateGradingReportsMutation,
} from '../../../db/api/generate-grading-reports.js';
import { getFileByIDQuery } from '../../../db/api/get-all-files.js';
import {
  markConversationAsReadMutation,
  streamConversationByIdSubscription,
  streamConversationWorkflowSubscription,
} from '../../../db/api/get-conversations.js';
import { getExamSubmissions } from '../../../db/api/get-exam-submissions.js';
import { getExamAdditionalInfoQuery, getExamTasksTreeQuery } from '../../../db/api/get-exam-tasks-tree.js';
import {
  getExamByIdQuery,
  getExamStateByExamIdQuery,
  streamExamStateWithTasksByExamIdSubscription,
} from '../../../db/api/get-exams.js';
import { getMasterSolutionsQuery } from '../../../db/api/get-master-solutions.js';
import { streamSubmissionsByExamIdSubscription } from '../../../db/api/get-submissions.js';
import { getTeacherByUserIdQuery } from '../../../db/api/get-teacher.js';
import {
  streamLiveGradingJobsByExamIdSubscription,
  type LiveGradingJobRow,
} from '../../../db/api/jobs/get-live-grading-jobs.js';
import {
  streamLiveSolutionJobsByExamIdSubscription,
  type LiveSolutionJobRow,
} from '../../../db/api/jobs/get-live-solution-jobs.js';
import { rerunBoundingBoxOCRMutation } from '../../../db/api/rerun-boundingbox-ocr.js';
import { saveAdditionalInfoMutation } from '../../../db/api/save-additional-info.js';
import { updateAssignmentProposalMessageMutation } from '../../../db/api/update-assignment-proposal.js';
import {
  addBoundingBoxByImageIdMutation,
  approveMsSectionMutation,
  deleteBoundingBoxbyImageIdMutation,
  deleteLanguageUseSchemeMutation,
  updateExamTaskGradingSchemeMutation,
  updateExamTaskMasterSolutionMutation,
  updateLanguageUseSchemeMembershipMutation,
  updateLanguageUseSchemeMutation,
} from '../../../db/api/update-exam-task-content.js';
import { updateGradingScaleMutation } from '../../../db/api/update-grading-scale.js';
import { updateGrammarErrorMutation } from '../../../db/api/update-grammar-error.js';
import { updateGrammarFiSettingsMutation } from '../../../db/api/update-grammar-fi-settings.js';
import { updateSubmissionImageIdsMutation } from '../../../db/api/update-submission-image-ids.js';
import {
  updateLanguageUseRuleResultMutation,
  updateSubmissionTaskMutation,
} from '../../../db/api/update-submission-task.js';
import { useExtractionSubscription } from '../../../db/api/useExtractionSubscription.js';
import { ConfirmationMessage, Conversation } from '../../../db/conversation.js';
import { Exam } from '../../../db/exam.js';
import { type Submission } from '../../../db/submission.js';
import { languageMap } from '../../../i18n.js';
import {
  GRADING_THRESHOLDS,
  STRUCTURE_READONLY_ON_CONFIRM,
  SUBMISSION_READONLY_ON_CONFIRM,
} from '../../lib/constants.js';
import { EXAM_UPDATE_EVENT, examEvents, triggerExamUpdate } from '../../lib/events.js';
import { getGuestUserID, setGuestConversationId } from '../../lib/guest-user-id.js';
import { buildSampleExamMessage, loadSampleExamFiles } from '../../lib/load-sample-exam.js';
import { SAMPLE_EXAMS } from '../../lib/sample-exams.js';
import { useCurrentUser } from '../../lib/useCurrentUser.js';
import { useCurrentUserProfile } from '../../lib/useCurrentUserProfile.js';
import { useVisibilityRecovery } from '../../lib/useVisibilityRecovery.js';
import { cn } from '../../lib/utils.js';
import { ChatCanvas } from '../canvas/chat-canvas.js';
import { collectTaskTreeNodeIds, findTaskTreeNode } from '../canvas/shared/task-tree.js';
import { DemoSelector } from '../canvas/views/demo/demo-selector.js';
import { ExamEditor } from '../canvas/views/exam-editor/exam-editor.js';
import { CombinedTaskCorrection } from '../canvas/views/grading/grading-view.js';
import {
  findBbxUncertainties,
  findLanguageUseUncertainties,
  findUncertainties,
  flattenSubTasksWithMeta,
  type ManualGrammarError,
} from '../canvas/views/grading/utils.js';
import { SolutionGradingCheck } from '../canvas/views/ms-gs/ms-gs-view.js';
import { StructureEditor, serializeStructureEditorTasks } from '../canvas/views/structure/structure-editor.js';
import { SubmissionReview } from '../canvas/views/submission-check/submission-review.js';
import { CorrectionSummary } from '../canvas/views/summary/summary-view.js';
import { ChatInput, type UiStepStatus } from '../chat-input/chat-input.js';
import { DashboardView } from '../dashboard/dashboard-view.js';
import { ChatMessages } from '../messages/chat-messages.js';

const convSelectionSet: any = {
  _id: 1,
  thinking: 1,
  language: 1,
  messages: {
    _id: 1,
    _class: 1,
    content: 1,
    baseContent: 1,
    active: 1,
    confirmed: 1,
    confirmedBy: 1,
    confirmationType: 1,
    schuelerabgaben: 1,
    musterloesung: 1,
    bewertungsschema: 1,
    leereKlausur: 1,
    allowBlankExam: 1,
  },
  hasUnreadReply: 1,
  suggestions: { _id: 1, label: 1, prompt: 1, userInputRequired: 1, icon: 1 },
  suggestionsDirty: 1,
  examId: 1,
  pendingCanvasOpen: 1,
  pendingDownload: 1,
};

const suggestionsSelectionSet: any = {
  _id: 1,
  suggestions: { _id: 1, label: 1, prompt: 1, userInputRequired: 1, isUpload: 1, icon: 1 },
  suggestionsDirty: 1,
  pendingCanvasOpen: 1,
  pendingDownload: 1,
};

/**
 * The flat task list only: `taskNumber` for the tab strip and `description` for the editor's text.
 *
 * Deliberately minimal. Its two callers (refreshTaskEditData and the initial task-list effect) read
 * nothing else, and the recursive task tree does NOT come through a selection set at all — it is fetched
 * whole as JSON by getExamTasksTreeQuery in fetchExamData, which is what the canvas and the grading views
 * use. A hand-written literal here could only ever mirror a fixed nesting depth (it used to stop at
 * subTasks, i.e. depth 2, against an arbitrarily deep model), and the examState block it used to carry was
 * read by nobody: the taskbar subscribes to the exam state itself in chat-input.tsx, and fetchExamData has
 * its own selection for the canvas. Keep it to what the callers actually use.
 */
const examTaskListSelectionSet: BaseQLSelectionSet<Exam> = {
  _id: 1,
  tasks: {
    taskNumber: 1,
    description: 1,
  },
};

const getByIDAny: any = getByID;

function cloneExamForState(value: any): Exam | null {
  if (!value) return null;
  if (typeof value?.toObject === 'function') {
    return new Exam(value.toObject());
  }
  return new Exam(value);
}

// languageUseGroupsJson holds every LanguageUseScheme a task belongs to (a task can belong to more than
// one) — patches the one entry matching schemeId and leaves the rest untouched, for the optimistic
// taskData update after a group save/approve.
function patchLanguageUseGroupsJson(
  rawJson: string | null | undefined,
  schemeId: string,
  patch: Record<string, unknown>,
): string | null {
  let groups: any[] = [];
  try {
    const parsed = rawJson ? JSON.parse(rawJson) : [];
    groups = Array.isArray(parsed) ? parsed : [];
  } catch {
    groups = [];
  }
  if (!groups.some(group => group?._id === schemeId)) return rawJson ?? null;
  return JSON.stringify(groups.map(group => (group?._id === schemeId ? { ...group, ...patch } : group)));
}

type CanvasView =
  | 'boundingbox'
  | 'correction'
  | 'msedit'
  | 'summary'
  | 'submissioncheck'
  | 'demoselector'
  | 'structureeditor'
  | 'examcreator';

const RING_SIZES = { normal: 28, active: 48 };
const RING_STROKE = 2.5;

function ProgressRing({ percent, isActive }: { percent: number; isActive: boolean }) {
  const size = isActive ? RING_SIZES.active : RING_SIZES.normal;
  const radius = (size - RING_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <svg className="absolute inset-0 pointer-events-none -rotate-90" width={size} height={size}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={RING_STROKE}
        className="text-border"
        strokeOpacity={0.3}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={RING_STROKE}
        className="text-brand-strong transition-all duration-500"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
      />
    </svg>
  );
}

function StepsIconSidebar({
  onStepClick,
  stepStatuses,
  stepPercents,
  stepLowConfs,
  stepCounts,
  showExamCreatorStep,
  showStructureStep,
  hideUploadStep,
  role,
}: {
  onStepClick: (view: CanvasView, data?: any) => void;
  stepStatuses: UiStepStatus[];
  stepPercents: number[];
  stepLowConfs: number[];
  stepCounts: { finished: number; total: number }[];
  /**
   * Whether index 0 of the arrays above is the exam-creator step rather than the upload step. Comes from
   * ChatInput, which builds those arrays (`showExamCreatorStep` there) — the status, percentage and counts
   * for it arrive through the very same arrays as every other step's, so there is nothing extra to pass.
   */
  showExamCreatorStep: boolean;
  /** Whether the arrays above carry an entry for the structure step — see `showStructureStep` in ChatInput. */
  showStructureStep: boolean;
  /** Whether this exam's answers arrived through the online student-exam flow — see
   * `exam.examState.onlineSubmissionReceived`. Hides the now-meaningless "upload" icon the same way a
   * student's account hides "student assignment" below. */
  hideUploadStep?: boolean;
  role?: 'teacher' | 'student';
}) {
  const { t } = useTranslation();
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLeaveTimer = () => {
    if (leaveTimerRef.current !== null) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  const handleEnter = (i: number) => {
    clearLeaveTimer();
    setHoveredStep(i);
  };

  const handleLeave = () => {
    leaveTimerRef.current = setTimeout(() => setHoveredStep(null), 150);
  };

  const uploadSteps: {
    icon: React.ElementType;
    labelKey: string;
    category: CanvasView | null;
    /** Set ONLY on the upload step, so `visibleSteps` below can single it out without disturbing
     * `category` — that field doubles as the click target (`onStepClick(step.category!, ...)`), and
     * "upload" is not a `CanvasView`, so it stays `null`/unclickable exactly as before. */
    isUploadStep?: boolean;
    data?: any;
  }[] = [
    { icon: CloudUpload, labelKey: 'ChatInput.steps.upload.label', category: null, isUploadStep: true },
    { icon: Users, labelKey: 'ChatInput.steps.studentAssignment.label', category: 'submissioncheck' },
    { icon: LayoutList, labelKey: 'ChatInput.steps.structure.label', category: 'structureeditor' },
    {
      icon: MessageSquare,
      labelKey: 'ChatInput.steps.answers.label',
      category: 'correction',
      data: { mode: 'answers' },
    },
    { icon: ClipboardList, labelKey: 'ChatInput.steps.masterSolution.label', category: 'msedit' },
    { icon: Key, labelKey: 'ChatInput.steps.gradingScheme.label', category: 'msedit' },
    { icon: PenLine, labelKey: 'ChatInput.steps.grading.label', category: 'correction', data: { mode: 'correction' } },
    { icon: BarChart2, labelKey: 'ChatInput.steps.summary.label', category: 'summary' },
  ];

  // The exam-creator step is not one of the upload pipeline's — it is prepended ahead of it, separated by
  // its own thin divider below. Its ENTRY is prepended here under the same flag ChatInput prepended its
  // status/percent/counts under, so index 0 lines up: nothing else in this loop treats it specially.
  const steps: typeof uploadSteps = showExamCreatorStep
    ? [
        { icon: Wand2, labelKey: 'ChatInput.steps.examCreator.label', category: 'examcreator', data: undefined },
        ...uploadSteps,
      ]
    : uploadSteps;

  // A student only ever uploads their own single exam — the "student assignment" (split) step is
  // meaningless for them. The structure step is hidden until it applies (see `showStructureStep` in
  // ChatInput — never shown for a generated exam, hidden by default until then). Both filtered the same
  // way ChatInput filters its own step list, so this stays index-aligned with the
  // stepStatuses/stepPercents/stepLowConfs/stepCounts arrays passed in here.
  const visibleSteps = steps.filter(
    s =>
      (role !== 'student' || s.category !== 'submissioncheck') &&
      (showStructureStep || s.category !== 'structureeditor') &&
      (!hideUploadStep || !s.isUploadStep),
  );

  return (
    <div className="fixed right-4 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center">
      {visibleSteps.map((step, i) => {
        const Icon = step.icon;
        const label = t(step.labelKey);
        const clickable = !!step.category;
        const status = stepStatuses[i] ?? 'pending';
        const lowConf = stepLowConfs[i] ?? 0;
        const isDone = status === 'complete' && lowConf === 0;
        const hasUncertainties = status === 'complete' && lowConf > 0;
        const isActive = status === 'loading';
        const percent = stepPercents[i] ?? (isDone ? 100 : 0);
        const stepCount = stepCounts[i] ?? { finished: 0, total: 0 };
        const showRing = clickable && (isActive || (percent > 0 && !isDone && !hasUncertainties));
        const isTooltipVisible = hoveredStep === i && clickable;
        // The exam-creator entry (only ever at i === 0, when shown) gets a small, fine horizontal rule
        // instead of the ordinary progress connector, to read as a separate step rather than the first
        // stage of the upload pipeline.
        const isExamCreatorDivider = showExamCreatorStep && i === 1;
        return (
          <div key={i} className="contents">
            {isExamCreatorDivider ? (
              <div className="h-px w-4 my-1 bg-border" />
            ) : (
              i > 0 && (
                <div
                  className={cn(
                    'w-px h-3 transition-colors duration-300',
                    isDone || hasUncertainties ? 'bg-brand-strong' : 'bg-sidebar-border',
                  )}
                />
              )
            )}
            <div
              className="relative flex items-center justify-center"
              onMouseEnter={() => handleEnter(i)}
              onMouseLeave={handleLeave}
            >
              <button
                type="button"
                onClick={() => clickable && onStepClick(step.category!, step.data)}
                disabled={!clickable}
                style={isActive ? { animation: 'breathe 2s ease-in-out infinite' } : undefined}
                className={cn(
                  'flex items-center justify-center rounded-full transition-all duration-300',
                  isActive ? 'w-12 h-12' : 'w-8 h-8',
                  showRing ? 'border-transparent' : 'border',
                  isActive
                    ? 'bg-background border-brand-strong text-brand-strong shadow-md cursor-pointer'
                    : isDone
                      ? 'bg-brand-strong border-brand-strong text-white shadow-sm hover:opacity-90 cursor-pointer'
                      : hasUncertainties
                        ? 'bg-brand-strong border-brand-strong text-white shadow-sm hover:opacity-90 cursor-pointer'
                        : clickable
                          ? 'bg-background border-brand-strong text-brand-strong shadow-sm hover:opacity-80 hover:shadow-md cursor-pointer'
                          : 'bg-background border-brand-strong text-brand-strong shadow-sm cursor-default',
                )}
              >
                <Icon
                  className={isActive ? 'h-5 w-5' : 'h-3.5 w-3.5'}
                  strokeWidth={isActive || isDone || hasUncertainties ? 2.5 : 2}
                />
              </button>
              {showRing && <ProgressRing percent={percent} isActive={isActive} />}
              {hasUncertainties && (
                <div className="pointer-events-none absolute -top-1 -right-1 z-10 flex items-center justify-center min-w-[16px] h-4 rounded-full bg-orange-500 text-white text-[9px] font-bold leading-none px-1 shadow-sm">
                  {lowConf > 99 ? '99+' : lowConf}
                </div>
              )}
              {clickable && (
                <div
                  className={cn(
                    'absolute right-full top-1/2 -translate-y-1/2 mr-2 z-50 overflow-hidden transition-all duration-300 ease-out',
                    isTooltipVisible ? 'max-w-64 opacity-100' : 'max-w-0 opacity-0 pointer-events-none',
                  )}
                  onMouseEnter={() => handleEnter(i)}
                  onMouseLeave={handleLeave}
                >
                  <button
                    type="button"
                    onClick={() => onStepClick(step.category!, step.data)}
                    className="whitespace-nowrap rounded-lg border border-border bg-white shadow-lg px-3 py-1.5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <p className="text-xs font-semibold text-gray-900">{label}</p>
                    {isActive && stepCount.total > 0 ? (
                      <p className="text-[10px] text-brand-strong font-medium mt-0.5">
                        {stepCount.finished}/{stepCount.total} abgeschlossen
                      </p>
                    ) : (
                      <p className="text-[10px] text-gray-400 mt-0.5">Details anzeigen</p>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Chat({
  conversationId: propConversationId,
  navigationVersion,
  onMessageSent,
  onOpenLogin,
  onOpenSignup,
  onConversationIdChange,
}: {
  conversationId?: string;
  navigationVersion?: number;
  onMessageSent?: (conversationId: string) => void;
  onOpenLogin?: () => void;
  onOpenSignup?: () => void;
  onConversationIdChange?: (conversationId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [conversationId, setConversationId] = useState<string>(propConversationId || 'new');
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [stepStatuses, setStepStatuses] = useState<UiStepStatus[]>([]);
  const [stepPercents, setStepPercents] = useState<number[]>([]);
  const [stepLowConfs, setStepLowConfs] = useState<number[]>([]);
  const [stepCounts, setStepCounts] = useState<{ finished: number; total: number }[]>([]);
  // Whether index 0 of the four arrays above is the exam-creator step. Owned by ChatInput, which builds
  // them from its own live exam-state subscription — the same one every other step's progress comes from.
  const [hasExamCreatorStep, setHasExamCreatorStep] = useState(false);
  // Whether the arrays above carry an entry for the structure step at all — see `showStructureStep` in
  // ChatInput. Same ownership as `hasExamCreatorStep` above.
  const [hasStructureStep, setHasStructureStep] = useState(false);
  const currentSuggestions = conversation?.suggestions || [];

  const { user } = useCurrentUser();
  const isGuest = !user;
  const { role } = useCurrentUserProfile();

  let examId = conversation?.examId || null;

  const watchedExamIdRef = useRef<string | null>(null);
  const watchedExamBootstrapRef = useRef<string | null>(null);
  const [examWatcherRestartKey, setExamWatcherRestartKey] = useState(0);
  const activeConversationIdRef = useRef<string>(conversationId);
  const isGenerateAnswerStreamingRef = useRef(false);
  useEffect(() => {
    activeConversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    if (examId && watchedExamBootstrapRef.current !== examId) {
      watchedExamBootstrapRef.current = examId;
      setExamWatcherRestartKey(key => key + 1);
    }
  }, [examId]);

  useEffect(() => {
    const watchedExamId = watchedExamIdRef.current;

    if (!examId) {
      if (watchedExamId) {
        try {
          stopExamWatcherMutation(watchedExamId)().catch(err => logger.error(err, `stopExamWatcher failed`));
        } catch (err) {
          logger.error(err, `stopExamWatcher call error`);
        }
      }
      watchedExamIdRef.current = null;
      return;
    }

    if (watchedExamId && watchedExamId !== examId) {
      try {
        stopExamWatcherMutation(watchedExamId)().catch(err => logger.error(err, `stopExamWatcher failed`));
      } catch (err) {
        logger.error(err, `stopExamWatcher call error`);
      }
      watchedExamIdRef.current = null;
    }

    if (!watchedExamIdRef.current || watchedExamIdRef.current !== examId) {
      try {
        startExamWatcherMutation(examId)().catch(err => logger.error(err, `startExamWatcher failed`));
        watchedExamIdRef.current = examId;
      } catch (err) {
        logger.error(err, `startExamWatcher call error`);
      }
    }

    return () => {
      const cur = watchedExamIdRef.current;
      if (cur) {
        try {
          stopExamWatcherMutation(cur)().catch(err => logger.error(err, `stopExamWatcher failed`));
        } catch (err) {
          logger.error(err, `stopExamWatcher call error`);
        }
        watchedExamIdRef.current = null;
      }
    };
  }, [examId, navigationVersion, examWatcherRestartKey]);

  useEffect(() => {
    if (!conversationId || conversationId === 'new') {
      return;
    }

    const subscription = streamConversationByIdSubscription(conversationId)(suggestionsSelectionSet).subscribe({
      next: (updatedConversation: any) => {
        setConversation(prevConversation => {
          if (!prevConversation) return prevConversation;

          const updated = new Conversation(prevConversation.toObject());
          (updated as any).suggestions = updatedConversation.suggestions;
          (updated as any).suggestionsDirty = updatedConversation.suggestionsDirty;
          (updated as any).pendingCanvasOpen = updatedConversation.pendingCanvasOpen ?? null;
          return updated;
        });
      },
      error: err => {
        logger.error(err, `Error in suggestion subscription:`);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId || conversationId === 'new') {
      return;
    }

    const subscription = streamConversationByIdSubscription(conversationId)(convSelectionSet).subscribe({
      next: (updatedConversation: any) => {
        if (isGenerateAnswerStreamingRef.current) {
          return;
        }
        if (!updatedConversation) return;

        setConversation(prev => {
          const next = (updatedConversation as Conversation).toObject();
          if (!prev) return new Conversation(next);
          const prevObj = (prev as Conversation).toObject();
          const merged = { ...prevObj, ...next, messages: next.messages ?? prevObj.messages };
          return new Conversation(merged as any);
        });

        if (updatedConversation?.hasUnreadReply === true) {
          logger.info(
            `Chat: active conversation has unread reply, clearing directly from conversation stream ${JSON.stringify({
              conversationId,
              examId: String(updatedConversation?.examId || ''),
            })}`,
          );
          void markConversationAsReadMutation(conversationId)().catch(err =>
            logger.warn(err, `Chat: failed to mark active conversation as read from conversation stream`),
          );
        }
      },
      error: err => {
        logger.error(err, `Error in active conversation subscription:`);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [conversationId]);

  // Standing subscription for server-initiated workflow streams (e.g. background grading finished).
  // The workflow agent runs the same streaming loop as generateAnswer but pushes through the
  // in-memory event bus, so the follow-up answer streams token-by-token here instead of "flying in".
  // It never overlaps a user turn (mutually exclusive via the server-side thinking-lock), so no
  // isGenerateAnswerStreamingRef guard is needed.
  useEffect(() => {
    if (!conversationId || conversationId === 'new') {
      return;
    }

    const subscription = streamConversationWorkflowSubscription(conversationId)(convSelectionSet).subscribe({
      next: (updatedConversation: any) => {
        if (!updatedConversation) return;

        setConversation(prev => {
          const next = (updatedConversation as Conversation).toObject();
          if (!prev) return new Conversation(next);
          const prevObj = (prev as Conversation).toObject();
          const merged = { ...prevObj, ...next, messages: next.messages ?? prevObj.messages };
          return new Conversation(merged as any);
        });
      },
      error: err => {
        logger.error(err, `Error in workflow conversation subscription:`);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [conversationId]);

  const [, setStudents] = useState<{ id: string; name: string; rawData: any }[]>([]);
  const [tasks, setTasks] = useState<{ id: string; name: string; taskNumber: string; needsAnswer?: boolean }[]>([]);
  const [taskdata, setTaskData] = useState<Record<string, any>>({});
  const [, setBoundingBoxes] = useState<any[]>([]);
  const [pageUrls, setPageUrls] = useState<string[]>([]);

  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [selectedTaskNumber, setSelectedTaskNumber] = useState<string>('');
  const [isMsLoading, setIsMsLoading] = useState(false);
  // Task/student currently visible in the correction canvas — purely informational, reported to the
  // chat agent so it knows what the teacher is looking at. Never used to target MS/GS edits.
  const [visibleCorrectionTask, setVisibleCorrectionTask] = useState<{
    taskNumber: string | null;
    studentId: string | null;
    studentName: string | null;
  }>({ taskNumber: null, studentId: null, studentName: null });

  const [canvasState, setCanvasState] = useState<{
    isOpen: boolean;
    view: CanvasView | null;
    data?: any;
  }>({
    isOpen: false,
    view: null,
  });
  const [correctionHeaderContent, setCorrectionHeaderContent] = useState<ReactNode>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isDownloadingReports, setIsDownloadingReports] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [demoLoadingId, setDemoLoadingId] = useState<string | null>(null);
  const [pendingDemoMessage, setPendingDemoMessage] = useState<string | null>(null);

  const previousSplitConfirmationRef = useRef<boolean | null>(null);
  const previousStructureConfirmationRef = useRef<boolean | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [enhancedSubmissions, setEnhancedSubmissions] = useState<any[]>([]);
  const [exam, setExam] = useState<Exam | null>(null);
  // Kept separate from `exam` (which only refreshes on mount/focus/explicit refetch) and updated via its
  // own live subscription below — the ms-gs canvas's delete-language-use-rule guard needs this to flip
  // the instant a grading run starts, not just whenever the canvas happens to refetch.
  const [isGradingInProgress, setIsGradingInProgress] = useState(false);
  // Raw live msExtraction/msGeneration/msRefine/gsExtraction/gsGeneration/gsRefine job rows (see
  // streamLiveSolutionJobsByExamIdSubscription below) — kept as the raw rows, not the derived per-box Sets,
  // because deriving needs `tasks` (to resolve a refine row's taskRefs, or to "spin everything" for an
  // extraction row) and `tasks` loads independently (only once the ms-gs canvas has opened at least once —
  // see refreshMsEditData). Deriving inside the subscription callback itself froze the Sets at whatever
  // `tasks` was AT THAT MOMENT: opening the canvas mid-extraction meant the callback ran while `tasks` was
  // still empty, computed empty Sets, and never re-ran again until the job row disappeared (already done)
  // — so the spinner never showed for that whole window. Recomputing via useMemo below re-derives the
  // moment either input changes, whichever lands second.
  const [liveSolutionJobRows, setLiveSolutionJobRows] = useState<string[]>([]);
  // Grading canvas box spinners — genuinely per (submission, task/scheme, dimension) for `grading`/
  // `languageUseGroupGrading`, unlike the MS/GS sets above. Keys are `${submissionId}::${taskKey}`
  // (content) / `${submissionId}::${schemeId}` (languageUse) — see
  // getLiveGradingJobsQuery/streamLiveGradingJobsByExamIdSubscription. Kept as raw rows (not the derived
  // Sets) for the same reason as `liveSolutionJobRows` above: a `gradingConsistency` row carries no
  // `submissionId` (see get-live-grading-jobs.ts's module doc) and must be fanned out to every submission
  // CURRENTLY known, so deriving needs `enhancedSubmissions` as a second input alongside the rows.
  const [liveGradingJobRows, setLiveGradingJobRows] = useState<string[]>([]);
  const [liveTaskExtractionProgress, setLiveTaskExtractionProgress] = useState<{
    status?: string;
    total?: number;
    finished?: number;
    message?: string;
  } | null>(null);
  const lastTaskExtractionProgressRef = useRef<string>('');
  const lastGsGenerationStatusRef = useRef<string>('');
  const panelRefsRef = useRef<
    Record<
      string,
      React.RefObject<{
        finalize: () => {
          baseContent: string;
          schuelerabgaben: string[];
          musterloesung?: string;
          bewertungsschema?: string;
          leereKlausur?: string;
        };
      }>
    >
  >({});

  const finalizeAssignmentDrafts = async (targetConversationId: string) => {
    const msgs = (conversation?.messages as any[]) || [];
    const updates = msgs.filter(
      m =>
        m?._class === 'AssignmentProposalMessage' &&
        (m?.active === true || typeof m?.active === 'undefined') &&
        typeof m?._id === 'string',
    );

    for (const msg of updates) {
      const messageId = String(msg._id);
      const panelRef = panelRefsRef.current[messageId];

      if (!panelRef) {
        logger.warn(`No panelRef found for messageId ${messageId}`);
        continue;
      }

      if (!panelRef.current) {
        logger.warn(`panelRef.current is null for messageId ${messageId}`);
        continue;
      }

      const finalState = panelRef.current.finalize();
      logger.info(`Finalized state: ${JSON.stringify(finalState)}`);

      // Do not change the visible message content on finalization; use existing message content
      const existingContent = String(msg.content || '');

      await updateAssignmentProposalMessageMutation(
        targetConversationId,
        messageId,
        existingContent,
        finalState.schuelerabgaben,
        finalState.musterloesung || '',
        finalState.bewertungsschema || '',
        finalState.leereKlausur || '',
        false,
        '',
      )();
    }
  };

  // Folds content AND language-use uncertainties into one count via the same central detectors used by
  // the "Grading" progress step (findUncertainties/findLanguageUseUncertainties), deduped by
  // submission/task — reused below to gate PDF report downloads so a task that's only
  // language-use-uncertain blocks the download too, instead of only content uncertainty being checked.
  const correctionUncertaintyCount = useMemo(() => {
    const examTasks = Array.isArray((exam as any)?.tasks) ? (exam as any).tasks : [];
    const languageUseSchemeRules = Array.isArray((exam as any)?.languageUseSchemeRules)
      ? (exam as any).languageUseSchemeRules
      : [];
    const needsAnswer = ({
      submissionIdx,
      mainTaskIdx,
      subTaskIdx,
    }: {
      submissionIdx: number;
      mainTaskIdx: number;
      subTaskIdx: number | null;
    }) => {
      const submission = (enhancedSubmissions as any)[submissionIdx];
      const mainTask = submission?.taskSubmissions?.[mainTaskIdx];
      if (!mainTask) return false;
      const mainTaskNumber = String((mainTask as any)?.taskNumber ?? '').trim();
      if (!mainTaskNumber) return false;
      let examNode = examTasks.find((et: any) => String(et?.taskNumber ?? '').trim() === mainTaskNumber);
      if (subTaskIdx === null) return examNode?.needsAnswer !== false;
      const entry = flattenSubTasksWithMeta(mainTask).find(e => e.flatIndex === subTaskIdx);
      if (!entry) return false;
      for (const seg of entry.numberPath) {
        const children = Array.isArray(examNode?.subTasks) ? examNode.subTasks : [];
        examNode = children.find((et: any) => String(et?.taskNumber ?? '').trim() === seg);
        if (examNode?.needsAnswer === false) return false;
      }
      return true;
    };

    const keyOf = ({
      submissionIdx,
      mainTaskIdx,
      subTaskIdx,
    }: {
      submissionIdx: number;
      mainTaskIdx: number;
      subTaskIdx: number | null;
    }) => `${submissionIdx}::${mainTaskIdx}::${subTaskIdx}`;

    const keys = new Set<string>();
    for (const u of findUncertainties(enhancedSubmissions as any).filter(needsAnswer)) keys.add(keyOf(u));
    for (const u of findLanguageUseUncertainties(enhancedSubmissions as any, languageUseSchemeRules).filter(
      needsAnswer,
    ))
      keys.add(keyOf(u));
    return keys.size;
  }, [enhancedSubmissions, exam]);

  const reportDownloadBlockedReason =
    submissions.length === 0
      ? t('Chat.alerts.noSubmissionsForReports')
      : enhancedSubmissions.length !== submissions.length ||
          enhancedSubmissions.some((submission: any) => {
            const mainTasks = Array.isArray(submission?.taskSubmissions) ? submission.taskSubmissions : [];
            return mainTasks.length === 0;
          }) ||
          correctionUncertaintyCount > 0
        ? t('Chat.alerts.reportsBlocked', { threshold: GRADING_THRESHOLDS.MEDIUM_CONFIDENCE * 100 })
        : null;

  const canDownloadReports = reportDownloadBlockedReason === null;

  const showGuestAuthButtons = isGuest && (onOpenLogin || onOpenSignup) && !canvasState.isOpen;

  const openCanvas = (view: CanvasView, data?: any) => {
    setCanvasState({ isOpen: true, view, data });
  };

  const onExtractionEventRef = useRef<any>(null);
  const onLowConfidenceRef = useRef<any>(null);
  const onGsCompletedRef = useRef<(() => void) | null>(null);
  // Fires on every relevant Exam-doc write while the ms-gs canvas is open — not only on GS-generation
  // completion (that's onGsCompletedRef). Piggybacks on the streamExamStateWithTasksByExamIdSubscription
  // handler below rather than opening a second watchQuery against the same {_id: examId} filter: relevance
  // is keyed on the filter, not the projection, so that subscription already re-fires on every write
  // ms-gs cares about (tasks, languageUseSchemeRules).
  const msEditRefreshTriggerRef = useRef<(() => void) | null>(null);

  onExtractionEventRef.current = async _message => {
    try {
      await refetchConversation();
    } catch (e) {
      logger.error(e, `Failed to refetch conversation from extraction subscription`);
    }
    try {
      await fetchExamData();
    } catch (e) {
      logger.error(e, `Failed to refetch exam data from extraction subscription`);
    }
  };

  onLowConfidenceRef.current = () => {
    // The canvas itself now opens via pendingCanvasOpen (set server-side alongside this same
    // hasLowConfidence result, see structure-step.ts) — that path survives an exam switch or a page
    // refresh, which this one-shot live event can't. Still refetch exam data here since low-confidence
    // detection means the task tree just changed underneath whatever's currently rendered.
    logger.info(`[ExtractionSubscription] hasIssues triggered`);
    try {
      void fetchExamData();
    } catch (e) {
      logger.error(e, `Failed to refetch exam data after low-confidence structure detection`);
    }
  };

  onGsCompletedRef.current =
    canvasState.isOpen && canvasState.view === 'msedit'
      ? () => {
          void refreshMsEditData(false);
        }
      : null;

  msEditRefreshTriggerRef.current =
    canvasState.isOpen && canvasState.view === 'msedit'
      ? () => {
          void refreshMsEditData(false);
        }
      : null;

  useExtractionSubscription(examId, conversationId, {
    onEvent: useCallback((msg: any) => onExtractionEventRef.current?.(msg), []),
    onLowConfidence: useCallback(() => onLowConfidenceRef.current?.(), []),
  });

  const refreshMsEditData = useCallback(
    async (showLoading = true) => {
      if (!examId) return;
      if (showLoading) setIsMsLoading(true);

      try {
        const dtos: any[] = await (getMasterSolutionsQuery as any)(examId)({
          taskId: 1,
          taskNumber: 1,
          displayTaskNumber: 1,
          taskType: 1,
          description: 1,
          masterSolutionContent: 1,
          gradingCriteria: 1,
          gradingMaxPoints: 1,
          descriptionImageIds: 1,
          descriptionBoxesJson: 1,
          masterSolutionImageIds: 1,
          gradingSchemeImageIds: 1,
          masterSolutionConfidence: 1,
          gradingSchemeConfidence: 1,
          masterSolutionIsApproved: 1,
          gradingSchemeIsApproved: 1,
          masterSolutionSource: 1,
          gradingSchemeSource: 1,
          descriptionContentType: 1,
          masterSolutionContentType: 1,
          gradingSchemeContentType: 1,
          needsAnswer: 1,
          languageUseGroupsJson: 1,
        });

        if (!dtos) return;

        const msTasks = dtos.map((dto: any, index: number) => {
          const displayTaskNumber = String(dto.displayTaskNumber ?? dto.taskNumber ?? index + 1);
          const taskId = String(dto.taskId ?? dto.taskNumber ?? displayTaskNumber);
          return {
            id: taskId,
            name: `Aufgabe ${displayTaskNumber}`,
            taskNumber: displayTaskNumber,
            taskType: typeof dto.taskType === 'string' ? dto.taskType : undefined,
            masterSolutionConfidence:
              typeof dto.masterSolutionConfidence === 'number' ? dto.masterSolutionConfidence : undefined,
            gradingSchemeConfidence:
              typeof dto.gradingSchemeConfidence === 'number' ? dto.gradingSchemeConfidence : undefined,
            masterSolutionIsApproved: dto.masterSolutionIsApproved === true,
            gradingSchemeIsApproved: dto.gradingSchemeIsApproved === true,
            masterSolutionSource: dto.masterSolutionSource || undefined,
            gradingSchemeSource: dto.gradingSchemeSource || undefined,
            needsAnswer: dto.needsAnswer !== false,
          };
        });
        setTasks(msTasks);

        const hasSelectedTaskInMsTasks =
          Boolean(selectedTaskNumber) && msTasks.some(task => task.id === selectedTaskNumber);
        if (!hasSelectedTaskInMsTasks && msTasks[0]?.id) {
          setSelectedTaskNumber(msTasks[0].id);
        }

        setTaskData(prevData => {
          const updatedTaskData = { ...prevData };

          dtos.forEach((dto: any, index: number) => {
            const taskId = String(dto.taskId ?? dto.taskNumber ?? index + 1);
            updatedTaskData[taskId] = {
              ...(updatedTaskData[taskId] || {}),
              // These fields come from a full, authoritative per-task snapshot (getMasterSolutionsQuery
              // always computes them, never a partial patch) — assigned directly, NOT falling back to
              // the stale cached value on empty/null. A fallback here would mean a legitimate transition
              // to empty (e.g. a task's own language-use rule cleared because it just joined a group) never
              // reaches the UI until a full reload rebuilds this cache from nothing. Contrast with the
              // image-id arrays below, which keep their old-value fallback for a documented, different
              // reason (a fresh fetch racing a just-completed upload/delete).
              taskType: dto.taskType,
              extractedText: dto.description || '',
              masterSolution: dto.masterSolutionContent || '',
              gradingCriteria: dto.gradingCriteria || '',
              gradingMaxPoints:
                typeof dto.gradingMaxPoints === 'number'
                  ? dto.gradingMaxPoints
                  : (updatedTaskData[taskId]?.gradingMaxPoints ?? 0),
              imageIds:
                Array.isArray(dto.descriptionImageIds) && dto.descriptionImageIds.length > 0
                  ? dto.descriptionImageIds
                  : updatedTaskData[taskId]?.imageIds || [],
              descriptionBoxesJson: dto.descriptionBoxesJson || updatedTaskData[taskId]?.descriptionBoxesJson || null,
              masterSolutionImageIds:
                Array.isArray(dto.masterSolutionImageIds) && dto.masterSolutionImageIds.length > 0
                  ? dto.masterSolutionImageIds
                  : updatedTaskData[taskId]?.masterSolutionImageIds || [],
              gradingSchemeImageIds:
                Array.isArray(dto.gradingSchemeImageIds) && dto.gradingSchemeImageIds.length > 0
                  ? dto.gradingSchemeImageIds
                  : updatedTaskData[taskId]?.gradingSchemeImageIds || [],
              masterSolutionConfidence:
                typeof dto.masterSolutionConfidence === 'number'
                  ? dto.masterSolutionConfidence
                  : updatedTaskData[taskId]?.masterSolutionConfidence,
              gradingSchemeConfidence:
                typeof dto.gradingSchemeConfidence === 'number'
                  ? dto.gradingSchemeConfidence
                  : updatedTaskData[taskId]?.gradingSchemeConfidence,
              masterSolutionIsApproved:
                dto.masterSolutionIsApproved === true || updatedTaskData[taskId]?.masterSolutionIsApproved === true,
              gradingSchemeIsApproved:
                dto.gradingSchemeIsApproved === true || updatedTaskData[taskId]?.gradingSchemeIsApproved === true,
              masterSolutionSource: dto.masterSolutionSource || undefined,
              gradingSchemeSource: dto.gradingSchemeSource || undefined,
              descriptionContentType: dto.descriptionContentType,
              masterSolutionContentType: dto.masterSolutionContentType,
              gradingSchemeContentType: dto.gradingSchemeContentType,
              needsAnswer: dto.needsAnswer !== false,
              languageUseGroupsJson: dto.languageUseGroupsJson ?? null,
            };
          });

          return updatedTaskData;
        });
      } catch (err) {
        logger.error(err, `Error fetching master solutions:`);
      } finally {
        if (showLoading) setIsMsLoading(false);
      }
    },
    [examId, selectedTaskNumber],
  );

  const refreshTaskEditData = useCallback(async () => {
    if (!examId) return;

    try {
      const exam: any = await getByID(Exam, examId)(examTaskListSelectionSet);

      if (!exam || !Array.isArray(exam.tasks)) return;

      const loadedTasks = exam.tasks.map((t: any, index: number) => {
        const tNum = t.taskNumber || `${index + 1}`;
        return {
          id: tNum,
          name: `Aufgabe ${tNum}`,
          taskNumber: tNum,
        };
      });

      setTasks(loadedTasks);

      setTaskData(prev => {
        const updated: Record<string, any> = { ...prev };
        exam.tasks.forEach((t: any, index: number) => {
          const tNum = t.taskNumber || `${index + 1}`;
          updated[tNum] = {
            ...(updated[tNum] || {}),
            extractedText: t.description || '',
          };
        });
        return updated;
      });
    } catch (err) {
      logger.error(err, `Error refreshing task edit data:`);
    }
  }, [examId]);

  const refetchConversation = useCallback(async () => {
    if (conversationId === 'new') {
      return;
    }
    if (isGenerateAnswerStreamingRef.current) {
      return;
    }

    const capturedConversationId = conversationId;

    try {
      const freshConversation: any = await getByIDAny(Conversation, capturedConversationId)(convSelectionSet);

      if (activeConversationIdRef.current !== capturedConversationId) return;

      if (!freshConversation) {
        setConversation(null);
        return;
      }
      setConversation(prev => {
        const next = (freshConversation as Conversation).toObject();
        if (!prev) return new Conversation(next);
        const prevObj = (prev as Conversation).toObject();
        const merged = { ...prevObj, ...next, messages: next.messages ?? prevObj.messages };
        return new Conversation(merged as any);
      });
    } catch (err) {
      logger.error(err, `Error refetching conversation:`);
    }
  }, [conversationId]);

  useVisibilityRecovery({
    shouldRecover: () => !!conversationId && conversationId !== 'new',
    onRecover: async () => {
      try {
        logger.info(`[Window Recovery] Refetching conversation on focus regain`);
        await refetchConversation();
      } catch (err) {
        logger.error(err, `Window Recovery: refetchConversation failed`);
      }

      try {
        logger.info(`[Window Recovery] Fetching exam data on focus regain`);
        await fetchExamData();
      } catch (err) {
        logger.error(err, `Window Recovery: fetchExamData failed`);
      }
    },
    recoveryDebounceMs: 1000,
  });

  const fetchExamData = useCallback(async () => {
    if (!examId) {
      setExam(null);
      setPageUrls([]);
      return;
    }

    try {
      const fetchedExam: any = await getExamByIdQuery(examId)({
        _id: 1,
        blankExamIds: 1,
        examSource: 1,
        examSourcePdfId: 1,
        // Read here, not derived from the tasks (which this query does not even fetch — see
        // `getExamTasksTreeQuery` below): the exam-creator read-only gate (`isExamCreatorReadOnly` in
        // renderCanvasView) needs the SAME signal the server enforces via `isExamCreatorLocked`.
        masterSolutionIds: 1,
        gradingSchemeIds: 1,
        figureAssets: { materialId: 1, fileId: 1 },
        examLayoutJson: 1,
        gradingScaleJson: 1,
        grammarFiSettingsJson: 1,
        languageUseSchemeRules: {
          _id: 1,
          criteria: 1,
          maxPoints: 1,
          confidence: 1,
          isApproved: 1,
          carriedOverFromGroup: 1,
          dependsOn: 1,
          displayNumber: 1,
        },
        subject: 1,
        examState: {
          metaDataSet: 1,
          examId: 1,
          masterSolutionProgress: { status: 1 } as any,
          gradingSchemeProgress: { status: 1 } as any,
          uiNeedsRefresh: 1,
          isCurrentlyGrading: 1,
          onlineSubmissionReceived: 1,
          taskExtractionProgress: {
            status: 1,
            total: 1,
            // `finishedUnits`, NOT `finished` — the counter was replaced by the set of done unit ids, and there
            // is no `finished` field on this type any more. See `BaseExtractionProgress`.
            finishedUnits: 1,
            message: 1,
          } as any,
          splitConfirmationState: { status: 1, confirmed: 1, messageId: 1, message: 1 } as any,
          structureConfirmationState: { status: 1, confirmed: 1, messageId: 1, message: 1 } as any,
          // Also for `isExamCreatorReadOnly` — see the note above `masterSolutionIds`. `submissionsFound`
          // alone can under-report (see exam-lock.ts's own note on it), so `answerExtractionProgress` is
          // read too — a submission created without going through the split flow still runs extraction.
          uploadProgress: { submissionsFound: 1 } as any,
          answerExtractionProgress: { status: 1, finishedUnits: 1 } as any,
        } as any,
      });

      let tasksTreeJson = '[]';
      let additionalInfoJson = '[]';
      try {
        [tasksTreeJson, additionalInfoJson] = await Promise.all([
          getExamTasksTreeQuery(examId)(),
          getExamAdditionalInfoQuery(examId)(),
        ]);
      } catch (err) {
        logger.error(err, `Error fetching tasks tree or additional info:`);
      }

      let tasksParsed: any[] = [];
      try {
        tasksParsed = JSON.parse(typeof tasksTreeJson === 'string' ? tasksTreeJson : String(tasksTreeJson));
      } catch (err) {
        logger.error(err, `Failed to parse tasks tree JSON: ${tasksTreeJson}`);
        tasksParsed = [];
      }

      let additionalInfoParsed: any[] = [];
      try {
        additionalInfoParsed = JSON.parse(
          typeof additionalInfoJson === 'string' ? additionalInfoJson : String(additionalInfoJson),
        );
      } catch (err) {
        logger.error(err, `Failed to parse additional info JSON: ${additionalInfoJson}`);
        additionalInfoParsed = [];
      }

      fetchedExam.tasks = tasksParsed;
      fetchedExam.additionalInformation = additionalInfoParsed;

      setExam(cloneExamForState(fetchedExam));
      setIsGradingInProgress(Number(fetchedExam?.examState?.isCurrentlyGrading ?? 0) > 0);

      const blankImageIds = Array.isArray(fetchedExam?.blankExamIds) ? fetchedExam.blankExamIds : [];
      if (blankImageIds.length === 0) {
        setPageUrls([]);
      } else {
        const pageLinks = await Promise.all(
          blankImageIds.map((imageId: string) =>
            getFileByIDQuery(imageId)({ _id: 1, meta: { fileName: 1, extension: 1 } })
              .then(file => file.getServeLink())
              .catch(() => ''),
          ),
        );
        setPageUrls(pageLinks.filter(Boolean));
      }
    } catch (fetchError) {
      logger.error(fetchError, `Error fetching exam:`);
      setExam(null);
      setPageUrls([]);
    }
  }, [examId]);

  // The teacher's default always-use-FI choice, needed to resolve the language-use grading mode for the
  // summary canvas (FI panel vs. rules-mode info) together with the exam's own override.
  const [teacherAlwaysUseFi, setTeacherAlwaysUseFi] = useState<boolean | null>(null);

  useEffect(() => {
    const userId = (user as any)?._id;
    if (!userId) {
      setTeacherAlwaysUseFi(null);
      return;
    }
    let isMounted = true;
    void getTeacherByUserIdQuery(userId)({ grammarPreferences: { alwaysUseFi: 1 } })
      .then(teachers => {
        if (!isMounted) return;
        const teacher: any = teachers?.[0] ?? null;
        setTeacherAlwaysUseFi(Boolean(teacher?.grammarPreferences?.alwaysUseFi));
      })
      .catch(err => logger.error({ err, context: 'chat' }, 'Failed to load teacher grammar preferences'));
    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    const nextId = propConversationId || 'new';
    if (nextId !== conversationId) {
      setConversationId(nextId);
      setConversation(null);
      setCanvasState({ isOpen: false, view: null });
      setStudents([]);
      setSubmissions([]);
      setBoundingBoxes([]);
      setPageUrls([]);
      setSelectedStudentId('');
      setSelectedTaskNumber('');
      setTasks([]);
      setTaskData({});
      isGenerateAnswerStreamingRef.current = false;
      setIsLoading(false);
      setError(null);
    }
  }, [propConversationId, navigationVersion]);

  useEffect(() => {
    setStudents([]);
    setSubmissions([]);
    setBoundingBoxes([]);
    setPageUrls([]);
    setSelectedStudentId('');
    setSelectedTaskNumber('');
  }, [examId]);

  useEffect(() => {
    if (conversationId === 'new') {
      setConversation(null);
      return;
    }

    getByIDAny(
      Conversation,
      conversationId,
    )(convSelectionSet).then((conversation: any) => {
      if (!conversation) {
        setConversation(null);
        return;
      }
      setConversation(prev => {
        const next = (conversation as Conversation).toObject();
        if (!prev) return new Conversation(next);
        const prevObj = (prev as Conversation).toObject();
        const merged = { ...prevObj, ...next, messages: next.messages ?? prevObj.messages };
        return new Conversation(merged as any);
      });
    });
  }, [conversationId]);

  useEffect(() => {
    void fetchExamData();
  }, [fetchExamData]);

  const fetchSubmissions = useCallback(() => {
    if (!examId) {
      setSubmissions([]);
      return;
    }

    getExamSubmissions(examId)
      .then(fetchedSubmissions => {
        setSubmissions(fetchedSubmissions as Submission[]);
      })
      .catch(error => {
        logger.error(error, `Error fetching submissions:`);
        setSubmissions([]);
      });
  }, [examId]);

  useEffect(() => {
    if (!examId) {
      setSubmissions([]);
      return;
    }

    const buildSubmissionTaskSelection = (depth: number): any => {
      const baseSelection: any = {
        _id: 1,
        taskNumber: 1,
        studentAnswer: 1,
        gradingState: 1,
        gradingResult: {
          score: 1,
          actualPointsGiven: 1,
          explanation: 1,
          confidence: 1,
          isApproved: 1,
        },
        boundingBoxes: {
          pageNumber: 1,
          imageId: 1,
          topLeft: { x: 1, y: 1 },
          bottomRight: { x: 1, y: 1 },
          confidence: 1,
          approved: 1,
          ocrExtractedText: 1,
          contentType: 1,
          ocrRerunning: 1,
        },
        grammarEvaluation: {
          status: 1,
          errorCount: 1,
          fehlerindex: 1,
        },
      };

      if (depth > 0) {
        baseSelection.subTasksSubmission = buildSubmissionTaskSelection(depth - 1);
      }

      return baseSelection;
    };

    const subscription = streamSubmissionsByExamIdSubscription(examId)({
      _id: 1,
      studentName: 1,
      studentId: 1,
      imageIds: 1,
      splitConfirmed: 1,
      languageUseRuleResults: {
        _id: 1,
        languageUseSchemeId: 1,
        score: 1,
        explanation: 1,
        confidence: 1,
        reviewerFeedback: 1,
        isApproved: 1,
        actualPointsGiven: 1,
        computedAt: 1,
      },
      taskSubmissions: buildSubmissionTaskSelection(5),
    } as any).subscribe({
      next: liveSubmissions => {
        fetchSubmissions();
      },
      error: err => {
        logger.error(err, `Fehler bei der Submission Live-Subscription:`);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [examId]);

  // Grading canvas box spinners (Tracks C/D) — only subscribed while the grading canvas is actually open,
  // matching the gating other correction-specific effects already use (canvasState.view === 'correction').
  useEffect(() => {
    if (!examId || !canvasState.isOpen || canvasState.view !== 'correction') {
      setLiveGradingJobRows([]);
      return;
    }

    const subscription = streamLiveGradingJobsByExamIdSubscription(examId)().subscribe({
      next: rows => setLiveGradingJobRows(Array.isArray(rows) ? rows : []),
      error: err => {
        logger.error(err, `Fehler bei der Live-Grading-Job-Subscription:`);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [examId, canvasState.isOpen, canvasState.view]);

  // Derives the two per-box Sets from the raw rows above, re-running whenever EITHER `liveGradingJobRows`
  // or `enhancedSubmissions` changes — a `gradingConsistency` row has no `submissionId` of its own to key
  // on (it reconciles one task across the whole wave), so it's fanned out to every submission currently
  // loaded instead. Mirrors the raw-rows-plus-useMemo pattern the MS/GS solution-jobs Sets above already
  // use, for the same reason: the fan-out input (`enhancedSubmissions`) loads independently of the
  // subscription, so deriving inside the subscription callback itself would freeze it at whatever
  // submissions were loaded the moment a row first arrived.
  //
  // QUEUED IS TRACKED SEPARATELY FROM RUNNING. A wave's rows are all created at dispatch, but only
  // `JOB_CONCURRENCY` of them can be claimed at once, so on a cohort every box would otherwise claim to be
  // grading while most are still waiting their turn. The `*Keys` Sets stay the UNION (anything live), so every
  // existing consumer is unchanged; the `queued*` Sets carry the subset that has not started, and `claimed`
  // wins over `pending` whenever both exist for one key — a task with one row running and one queued behind it
  // is running.
  const { regradingContentKeys, regradingLanguageUseKeys, queuedContentKeys, queuedLanguageUseKeys } = useMemo(() => {
    const contentKeys = new Set<string>();
    const languageUseKeys = new Set<string>();
    const runningContentKeys = new Set<string>();
    const runningLanguageUseKeys = new Set<string>();
    const allSubmissionIds = (enhancedSubmissions as any[])
      .map(submission => String(submission?._id ?? ''))
      .filter(Boolean);

    for (const raw of liveGradingJobRows) {
      let row: LiveGradingJobRow;
      try {
        row = JSON.parse(raw);
      } catch {
        continue;
      }
      // Absent status means an older server that did not project it — treat as running, which is what the
      // spinner did for every live row before there was a distinction.
      const isRunning = row.status !== 'pending';
      // WHICH BOXES THIS ROW COVERS. A row's `taskKey` is the MAIN TASK even when the regrade is scoped to one
      // subtask, so keying on it alone lit up every sibling under that root. When the row names refs, key on
      // those instead and let the consumer match its own node against them (see use-grading-state's
      // `matchesRegradeKeys`); with no refs the row covers the whole main task, which is the root key.
      const contentKeysOf = (submissionId: string): string[] =>
        row.taskRefs?.length
          ? row.taskRefs.map(ref => `${submissionId}::ref:${normalizeTaskRef(ref)}`)
          : [`${submissionId}::${row.taskKey}`];
      if (row.type === 'gradingConsistency') {
        // Content-only (see grading-consistency.job.ts's `units`: a languageUse-target wave enumerates no
        // consistency row) and task-scoped across the whole wave rather than one submission — so a live row spins
        // every submission's content box for this task, the same coarser-than-precise tradeoff the MS/GS
        // extraction spinner already accepts for the same reason (no per-unit id to key on).
        if (row.taskKey) {
          allSubmissionIds.forEach(submissionId => {
            for (const key of contentKeysOf(submissionId)) {
              contentKeys.add(key);
              if (isRunning) runningContentKeys.add(key);
            }
          });
        }
        continue;
      }
      if (!row.submissionId) continue;
      if (row.taskKey && (!row.target || row.target === 'content')) {
        for (const key of contentKeysOf(row.submissionId)) {
          contentKeys.add(key);
          if (isRunning) runningContentKeys.add(key);
        }
      }
      if (row.schemeId) {
        languageUseKeys.add(`${row.submissionId}::${row.schemeId}`);
        if (isRunning) runningLanguageUseKeys.add(`${row.submissionId}::${row.schemeId}`);
      }
    }

    const queuedOnly = (live: Set<string>, running: Set<string>): Set<string> =>
      new Set([...live].filter(key => !running.has(key)));

    return {
      regradingContentKeys: contentKeys,
      regradingLanguageUseKeys: languageUseKeys,
      queuedContentKeys: queuedOnly(contentKeys, runningContentKeys),
      queuedLanguageUseKeys: queuedOnly(languageUseKeys, runningLanguageUseKeys),
    };
  }, [liveGradingJobRows, enhancedSubmissions]);

  // MS/GS canvas box spinners (Track B) — sourced entirely from live msExtraction/msGeneration/msRefine/
  // gsExtraction/gsGeneration/gsRefine job rows (see get-live-solution-jobs.ts's module doc for why:
  // SolutionProgress can't distinguish a content refine from a languageUse one, and extraction's per-node
  // precision doesn't show up in practice anyway). Not gated to canvasState — both the ms-gs canvas and
  // the grading view's read-only GS echo need it, matching the ungated exam-state effect below.
  useEffect(() => {
    if (!examId) {
      setLiveSolutionJobRows([]);
      return;
    }

    const subscription = streamLiveSolutionJobsByExamIdSubscription(examId)().subscribe({
      next: rows => setLiveSolutionJobRows(Array.isArray(rows) ? rows : []),
      error: err => {
        logger.error(err, `Fehler bei der Live-Solution-Job-Subscription:`);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [examId]);

  // Derives the three per-box Sets from the raw rows above, re-running whenever EITHER `liveSolutionJobRows`
  // or `exam` changes — see the state doc comment for why this can't just happen inside the subscription
  // callback (the tree loads independently and can still be stale/empty when a row first arrives).
  //
  // Resolves against `exam.tasks` (the nested tree, real `_id`s, loaded independently of which canvas has
  // been opened), NOT the flat `tasks` state ms-gs's own DTO list uses. `tasks` turned out to carry
  // INCOMPATIBLE id semantics depending on which of several refresh functions last wrote to it —
  // `refreshMsEditData` (ms-gs canvas only) sets real `_id`s, but `refreshTaskEditData` and the mount
  // effect (which run for the grading canvas) set the taskNumber string instead. A teacher who opened the
  // grading canvas without ever opening ms-gs would silently never get a match. `exam.tasks` has no such
  // split — it's the same tree `use-grading-state.ts` already trusts for its own id lookups.
  const { refiningMasterSolutionTaskIds, refiningGradingSchemeTaskIds, refiningGradingSchemeLanguageUseTaskIds } =
    useMemo(() => {
      const msIds = new Set<string>();
      const gsContentIds = new Set<string>();
      const gsLanguageUseIds = new Set<string>();
      const examTasks: any[] = Array.isArray((exam as any)?.tasks) ? (exam as any).tasks : [];
      const allTaskIds = collectTaskTreeNodeIds(examTasks);

      // `ref` is dot-formatted (see get-live-solution-jobs.ts) — resolved via the same tree-walk
      // use-grading-state.ts uses for its pipe-separated `dependsOn` paths (see task-tree.ts).
      //
      // Resolves to the matched node's id AND every id under it, not just its own — a ref can (and, for
      // the MS→GS auto-cascade specifically, always does — see master-solution.step.ts's
      // mainTaskNumbersForIds, which only ever reports the ROOT a changed task sits under) name a root
      // whose subtree is what the server actually refines (refineMasterSolutionAgent/
      // refineGradingSchemeAgent both expand a matched ref via gradableSubtreeNodes). Resolving only the
      // exact node missed every subtask's box while its root-level cascade refine was genuinely touching it.
      const refToIds = (ref: string): string[] => {
        const node = findTaskTreeNode(examTasks, ref, '.');
        return node ? collectTaskTreeNodeIds([node]) : [];
      };

      for (const raw of liveSolutionJobRows) {
        let row: LiveSolutionJobRow;
        try {
          row = JSON.parse(raw);
        } catch {
          continue;
        }
        switch (row.type) {
          case 'msExtraction':
            allTaskIds.forEach(id => msIds.add(id));
            break;
          case 'gsExtraction':
            allTaskIds.forEach(id => {
              gsContentIds.add(id);
              gsLanguageUseIds.add(id);
            });
            break;
          case 'msGeneration':
            (row.targetTaskIds ?? []).forEach(id => msIds.add(id));
            break;
          case 'gsGeneration':
            (row.targetTaskIds ?? []).forEach(id => {
              gsContentIds.add(id);
              gsLanguageUseIds.add(id);
            });
            break;
          case 'msRefine':
            (row.taskRefs ?? []).forEach(ref => refToIds(ref).forEach(id => msIds.add(id)));
            break;
          case 'gsRefine': {
            const targetSet = row.target === 'languageUse' ? gsLanguageUseIds : gsContentIds;
            (row.taskRefs ?? []).forEach(ref => refToIds(ref).forEach(id => targetSet.add(id)));
            break;
          }
        }
      }

      return {
        refiningMasterSolutionTaskIds: msIds,
        refiningGradingSchemeTaskIds: gsContentIds,
        refiningGradingSchemeLanguageUseTaskIds: gsLanguageUseIds,
      };
    }, [liveSolutionJobRows, exam]);

  useEffect(() => {
    if (!examId) {
      setLiveTaskExtractionProgress(null);
      lastTaskExtractionProgressRef.current = '';
      lastGsGenerationStatusRef.current = '';
      setIsGradingInProgress(false);
      return;
    }

    const subscription = streamExamStateWithTasksByExamIdSubscription(examId)({
      masterSolutionProgress: { status: 1 } as any,
      gradingSchemeProgress: { status: 1 } as any,

      splitConfirmationState: {
        status: 1,
        confirmed: 1,
        messageId: 1,
        message: 1,
      } as any,
      structureConfirmationState: {
        status: 1,
        confirmed: 1,
        messageId: 1,
        message: 1,
      } as any,

      taskExtractionProgress: {
        status: 1,
        total: 1,
        finishedUnits: 1,
        message: 1,
      },
      tasks: {
        _id: 1,
        taskNumber: 1,
        description: 1,
        needsAnswer: 1,
        taskType: 1,
        declaredMaxPoints: 1,
        masterSolution: {
          content: 1,
          taskNumber: 1,
          examId: 1,
          feedback: 1,
        },
        gradingScheme: {
          criteria: 1,
          maxPoints: 1,
          examId: 1,
          feedback: 1,
        },
        boundingBoxes: {
          _id: 1,
          pageNumber: 1,
          imageId: 1,
          ocrExtractedText: 1,
          contentType: 1,
          confidence: 1,
          approved: 1,
          topLeft: { x: 1, y: 1 },
          bottomRight: { x: 1, y: 1 },
        },
        subTasks: {
          _id: 1,
          taskNumber: 1,
          description: 1,
          taskType: 1,
          declaredMaxPoints: 1,
          needsAnswer: 1,
          masterSolution: {
            content: 1,
            taskNumber: 1,
            examId: 1,
            feedback: 1,
          },
          gradingScheme: {
            criteria: 1,
            maxPoints: 1,
            examId: 1,
            feedback: 1,
          },
          boundingBoxes: {
            _id: 1,
            pageNumber: 1,
            imageId: 1,
            ocrExtractedText: 1,
            contentType: 1,
            confidence: 1,
            approved: 1,
            topLeft: { x: 1, y: 1 },
            bottomRight: { x: 1, y: 1 },
          },
          subTasks: {
            _id: 1,
            taskNumber: 1,
            description: 1,
            taskType: 1,
            declaredMaxPoints: 1,
            needsAnswer: 1,
            masterSolution: {
              content: 1,
              taskNumber: 1,
              examId: 1,
              feedback: 1,
            },
            gradingScheme: {
              criteria: 1,
              maxPoints: 1,
              examId: 1,
              feedback: 1,
            },
            boundingBoxes: {
              _id: 1,
              pageNumber: 1,
              imageId: 1,
              ocrExtractedText: 1,
              contentType: 1,
              confidence: 1,
              approved: 1,
              topLeft: { x: 1, y: 1 },
              bottomRight: { x: 1, y: 1 },
            },
          },
        },
      },
    } as any).subscribe({
      next: (examState: any) => {
        msEditRefreshTriggerRef.current?.();
        // Re-check isCurrentlyGrading via a plain query on every relevant exam write, rather than via its
        // own dedicated live subscription: that subscription (streamExamStateByExamIdSubscription) is
        // affected by a client-side subscription-caching bug in @adornis/baseql (SubscriptionDedupe +
        // completingShareReplay can freeze a shared subscription on its first-ever value after a
        // resubscribe), which was confirmed via targeted logging to leave this flag permanently wrong.
        // A one-off query has no such caching path, so piggybacking it on this already-reliable trigger
        // gives both correctness and near-instant freshness.
        void getExamStateByExamIdQuery(examId)({ isCurrentlyGrading: 1 } as any)
          .then(freshExamState => {
            setIsGradingInProgress(Number((freshExamState as any)?.isCurrentlyGrading ?? 0) > 0);
          })
          .catch(err => {
            logger.error(err, `Failed to refresh isCurrentlyGrading:`);
          });
        const progress = examState?.taskExtractionProgress;
        // COUNTED FROM THE SET, not read as a number. The step records WHICH units are done (`finishedUnits`)
        // rather than how many, so `progress.finished` has been undefined since that change — which read as 0 of N
        // while the units were landing in Mongo. Same arithmetic as `getRegionStats` in `chat-helpers.ts`.
        //
        // COMPUTED ONCE, ABOVE BOTH USERS, because this handler has two: the live progress state just below and the
        // merged exam state further down. Only the first was counting from the set, so the merged copy — which is
        // what the structure editor reads — kept carrying `undefined`.
        const finished = Array.isArray(progress?.finishedUnits) ? progress.finishedUnits.length : 0;
        if (progress) {
          const snapshot = JSON.stringify({
            status: progress?.status,
            total: progress?.total,
            finished,
            message: progress?.message,
          });

          setLiveTaskExtractionProgress({
            status: progress?.status,
            total: typeof progress?.total === 'number' ? progress.total : Number(progress?.total ?? 0),
            finished,
            message: progress?.message,
          });

          if (snapshot !== lastTaskExtractionProgressRef.current) {
            lastTaskExtractionProgressRef.current = snapshot;
          }
        }

        const gsStatus = examState?.gradingSchemeProgress?.status;
        if (gsStatus) {
          const prev = lastGsGenerationStatusRef.current;
          if (gsStatus !== prev) {
            lastGsGenerationStatusRef.current = gsStatus;
            if (gsStatus === 'COMPLETED' && prev !== '') {
              void fetchExamData();
              onGsCompletedRef.current?.();
            }
          }
        }

        if (
          Array.isArray(examState?.tasks) ||
          examState?.splitConfirmationState ||
          examState?.structureConfirmationState
        ) {
          setExam(prev => {
            if (!prev) return prev;

            const prevData = typeof (prev as any).toObject === 'function' ? (prev as any).toObject() : prev;
            const incomingExamState = examState?.examState || {};
            const incomingSplitConfirmationState =
              examState?.splitConfirmationState ?? incomingExamState.splitConfirmationState;
            const incomingStructureConfirmationState =
              examState?.structureConfirmationState ?? incomingExamState.structureConfirmationState;

            return cloneExamForState({
              ...prevData,
              examState: {
                ...(prevData.examState || {}),
                ...incomingExamState,
                ...(incomingSplitConfirmationState ? { splitConfirmationState: incomingSplitConfirmationState } : {}),
                ...(incomingStructureConfirmationState
                  ? { structureConfirmationState: incomingStructureConfirmationState }
                  : {}),
                ...(progress
                  ? {
                      taskExtractionProgress: {
                        ...(prevData.examState?.taskExtractionProgress || {}),
                        status: progress?.status,
                        total: progress?.total,
                        // The counted value, and the SET it was counted from — readers of the merged state use
                        // either, and leaving `finishedUnits` out of the merge left them reading a stale copy
                        // from before this subscription event.
                        finished,
                        finishedUnits: Array.isArray(progress?.finishedUnits) ? progress.finishedUnits : [],
                        message: progress?.message,
                      },
                    }
                  : {}),
              },
              ...(Array.isArray(examState?.tasks) ? { tasks: examState.tasks } : {}),
            } as any);
          });
        }
      },
      error: err => {
        logger.error(err, `[StructureEditor Progress] examState subscription failed:`);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [examId, fetchExamData]);

  const handleUiRefreshRequested = useCallback(async () => {
    await refetchConversation();
    await fetchExamData();
    fetchSubmissions();

    if (canvasState.isOpen && canvasState.view === 'correction') {
      await refreshTaskEditData();
    }
  }, [canvasState.isOpen, canvasState.view, fetchExamData, fetchSubmissions, refetchConversation, refreshTaskEditData]);

  const handleDownloadReports = useCallback(async () => {
    if (!examId || isDownloadingReports) return;

    if (!canDownloadReports) {
      window.alert(
        reportDownloadBlockedReason ??
          t('Chat.alerts.reportsBlocked', { threshold: GRADING_THRESHOLDS.MEDIUM_CONFIDENCE * 100 }),
      );
      return;
    }

    setIsDownloadingReports(true);
    try {
      const reportIds = await generateGradingReportsMutation(examId)();

      if (!Array.isArray(reportIds) || reportIds.length === 0) {
        window.alert(t('Chat.alerts.noSubmissionsForReports'));
        return;
      }

      const reportFiles = await Promise.all(
        reportIds.map(reportId =>
          getFileByIDQuery(reportId)({
            _id: 1,
            meta: {
              fileName: 1,
              extension: 1,
            },
          }).catch(err => {
            logger.error(err, `Failed to fetch report file for id ${reportId}`);
            return null;
          }),
        ),
      );

      const validFiles = reportFiles.filter(Boolean);

      if (validFiles.length === 0) {
        window.alert('PDF-Berichte konnten nicht abgerufen werden. Bitte erneut versuchen.');
        return;
      }

      const file = validFiles[0];
      if (!file) return;

      const downloadUrl = file.getDownloadLink();
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      if (isSafari) {
        // Safari blocks a.click() after async awaits (user activation expired).
        // window.location.href requires no user activation; server sends
        // Content-Disposition: attachment so the page stays loaded.
        window.location.href = downloadUrl;
      } else {
        const fileName = file.meta?.fileName
          ? `${file.meta.fileName}${file.meta.extension ? `.${file.meta.extension}` : ''}`
          : 'report';
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (downloadError) {
      logger.error(downloadError, `Error generating grading reports:`);
      window.alert('PDF-Berichte konnten nicht erstellt werden. Bitte erneut versuchen.');
    } finally {
      setIsDownloadingReports(false);
    }
  }, [canDownloadReports, examId, isDownloadingReports, reportDownloadBlockedReason, t]);

  const handleDownloadGradebookCsv = useCallback(async () => {
    if (!examId || isDownloadingCsv) return;
    setIsDownloadingCsv(true);
    try {
      const fileId = await generateGradebookCsvMutation(examId)();
      if (!fileId) {
        window.alert('CSV konnte nicht erstellt werden. Bitte erneut versuchen.');
        return;
      }
      const file = await getFileByIDQuery(fileId)({ _id: 1, meta: { fileName: 1, extension: 1 } }).catch(() => null);
      if (!file) {
        window.alert('CSV-Datei konnte nicht abgerufen werden.');
        return;
      }
      const downloadUrl = file.getDownloadLink();
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        window.location.href = downloadUrl;
      } else {
        const fileName = file.meta?.fileName
          ? `${file.meta.fileName}${file.meta.extension ? `.${file.meta.extension}` : ''}`
          : 'notenuebersicht.csv';
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      logger.error(err, 'Error generating gradebook CSV');
      window.alert('CSV konnte nicht erstellt werden. Bitte erneut versuchen.');
    } finally {
      setIsDownloadingCsv(false);
    }
  }, [examId, isDownloadingCsv]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  useEffect(() => {
    if (!canvasState.isOpen) return;
    if (
      canvasState.view !== 'boundingbox' &&
      canvasState.view !== 'correction' &&
      canvasState.view !== 'summary' &&
      canvasState.view !== 'submissioncheck' &&
      canvasState.view !== 'structureeditor' &&
      canvasState.view !== 'examcreator'
    ) {
      return;
    }
    fetchSubmissions();
    if (canvasState.view === 'structureeditor' || canvasState.view === 'examcreator') {
      void fetchExamData();
    }
  }, [canvasState.isOpen, canvasState.view, fetchSubmissions, fetchExamData]);

  useEffect(() => {
    const handleExamUpdated = () => {
      void fetchExamData();
      fetchSubmissions();

      if (canvasState.isOpen && canvasState.view === 'correction') {
        void refreshTaskEditData();
      }
    };

    examEvents.addEventListener(EXAM_UPDATE_EVENT, handleExamUpdated);
    return () => {
      examEvents.removeEventListener(EXAM_UPDATE_EVENT, handleExamUpdated);
    };
  }, [canvasState.isOpen, canvasState.view, fetchExamData, fetchSubmissions, refreshTaskEditData]);

  useEffect(() => {
    if (!submissions || submissions.length === 0) {
      setStudents([]);
      setSelectedStudentId('');
      return;
    }

    const loadedStudents = submissions.map((s: any, index: number) => ({
      id: s._id,
      name: s.studentId ? `Student ${s.studentId}` : `Schüler ${index + 1}`,
      rawData: s,
    }));
    setStudents(loadedStudents);

    const selectedStillExists = loadedStudents.some(student => student.id === selectedStudentId);
    if ((!selectedStudentId || !selectedStillExists) && loadedStudents.length > 0) {
      setSelectedStudentId(loadedStudents[0]!.id);
    }
  }, [submissions, selectedStudentId]);

  useEffect(() => {
    if (!examId) return;

    let isMounted = true;

    getByID(
      Exam,
      examId,
    )(examTaskListSelectionSet).then((exam: any) => {
      if (!isMounted || !exam) return;

      const loadedTasks: any[] = [];
      const initialTaskData: Record<string, any> = {};

      if (exam.tasks && Array.isArray(exam.tasks)) {
        exam.tasks.forEach((t: any, index: number) => {
          const tNum = t.taskNumber || `${index + 1}`;
          loadedTasks.push({
            id: tNum,
            name: `Aufgabe ${tNum}`,
            taskNumber: tNum,
          });

          initialTaskData[tNum] = {
            masterSolution: '',
            extractedText: t.description || '',
          };
        });
        setTasks(loadedTasks);
      }

      const nextTaskNum = selectedTaskNumber || (loadedTasks[0] ? loadedTasks[0].id : '');
      if (!selectedTaskNumber && nextTaskNum) setSelectedTaskNumber(nextTaskNum);

      setTaskData(prev => {
        const merged: Record<string, any> = { ...prev };
        for (const [key, val] of Object.entries(initialTaskData)) {
          merged[key] = {
            ...(val as any),
            ...(merged[key] || {}),
          };
        }
        return merged;
      });
    });

    return () => {
      isMounted = false;
    };
  }, [examId]);

  useEffect(() => {
    if (!examId || canvasState.view !== 'msedit' || !canvasState.isOpen) return;
    void refreshMsEditData(true);
  }, [examId, canvasState.isOpen, canvasState.view, refreshMsEditData]);

  useEffect(() => {
    if (!submissions || submissions.length === 0) {
      setEnhancedSubmissions([]);
      return;
    }

    let isMounted = true;

    const buildEnhanced = async () => {
      const normalizeGradingResult = (raw: any) => {
        const source = raw?.doc || raw;
        if (!source || typeof source !== 'object') return undefined;

        const parsedScore =
          typeof source.score === 'number' ? source.score : typeof source.score === 'string' ? Number(source.score) : 0;
        const parsedActualPointsGiven =
          typeof source.actualPointsGiven === 'number'
            ? source.actualPointsGiven
            : typeof source.actualPointsGiven === 'string'
              ? Number(source.actualPointsGiven)
              : undefined;

        return {
          ...source,
          score: Number.isFinite(parsedScore) ? parsedScore : 0,
          actualPointsGiven: Number.isFinite(parsedActualPointsGiven as number) ? parsedActualPointsGiven : undefined,
          explanation: typeof source.explanation === 'string' ? source.explanation : '',
          confidence: source.confidence !== undefined ? String(source.confidence) : undefined,
          isApproved: source.isApproved === true,
        };
      };

      const enhanced = await Promise.all(
        submissions.map(async (sub: any, index: number) => {
          const imageIds = sub.imageIds || sub.imageIDs || [];
          let pages: string[] = [];
          if (imageIds.length > 0) {
            try {
              const files = await Promise.all(
                imageIds.map((id: string) => getByID(AdornisFile, id)(AdornisFile.allFields)),
              );
              pages = files.filter(f => f !== null).map((f: any) => f.getServeLink());
            } catch (e) {
              logger.error(e, `Fehler beim Laden der Bilder für: ${sub._id}`);
            }
          }

          const mapSubmissionTask = (task: any, fallbackNumber: string, numberPath: string[]): any => {
            const normalizedTaskNumber = String(task?.taskNumber ?? task?.tasknumber ?? fallbackNumber);
            const fullPath = [...numberPath, normalizedTaskNumber];
            const pathKey = fullPath.join('-');

            const mappedBoxes = (task?.boundingBoxes || []).map((box: any, boxIndex: number) => {
              const rawX1 = box?.topLeft?.x;
              const rawY1 = box?.topLeft?.y;
              const rawX2 = box?.bottomRight?.x;
              const rawY2 = box?.bottomRight?.y;

              let x = 0;
              let y = 0;
              let width = 0;
              let height = 0;
              if (rawX1 !== undefined && rawY1 !== undefined && rawX2 !== undefined && rawY2 !== undefined) {
                const x1 = rawX1 / 10;
                const y1 = rawY1 / 10;
                const x2 = rawX2 / 10;
                const y2 = rawY2 / 10;
                x = Math.min(x1, x2);
                y = Math.min(y1, y2);
                width = Math.abs(x2 - x1);
                height = Math.abs(y2 - y1);
              }

              const rawPageNumber = box?.pageNumber;
              const pageNumber =
                typeof rawPageNumber === 'number' ? (rawPageNumber >= 1 ? rawPageNumber - 1 : rawPageNumber) : 0;

              return {
                id: `box-${pathKey}-${boxIndex}`,
                x,
                y,
                width,
                height,
                pageNumber,
                confidence:
                  typeof box?.confidence === 'number' && Number.isFinite(box.confidence) ? box.confidence : undefined,
                approved: box?.approved === true,
                ocrExtractedText: typeof box?.ocrExtractedText === 'string' ? box.ocrExtractedText : '',
                contentType: box?.contentType,
                ocrRerunning: box?.ocrRerunning === true,
              };
            });

            // Grammar errors: convert each located box from the DB 0–1000 scale to 0–100 percentages
            // (same as task boxes). Resolve the page index by imageId against this submission's pages
            // (robust), falling back to the box's normalized pageNumber.
            const rawGrammar = (task?.grammarEvaluation as any)?.doc || task?.grammarEvaluation;
            let grammarEvaluation: any = undefined;
            if (rawGrammar) {
              const grammarErrors = (rawGrammar.errors || []).map((err: any, errIndex: number) => {
                const gb = err?.boundingBox;
                let box: any = null;
                if (gb?.topLeft && gb?.bottomRight) {
                  const x1 = (gb.topLeft.x ?? 0) / 10;
                  const y1 = (gb.topLeft.y ?? 0) / 10;
                  const x2 = (gb.bottomRight.x ?? 0) / 10;
                  const y2 = (gb.bottomRight.y ?? 0) / 10;
                  const byImageId = gb.imageId ? imageIds.indexOf(gb.imageId) : -1;
                  const pageNumber =
                    byImageId >= 0
                      ? byImageId
                      : typeof gb.pageNumber === 'number'
                        ? gb.pageNumber >= 1
                          ? gb.pageNumber - 1
                          : gb.pageNumber
                        : 0;
                  box = {
                    x: Math.min(x1, x2),
                    y: Math.min(y1, y2),
                    width: Math.abs(x2 - x1),
                    height: Math.abs(y2 - y1),
                    pageNumber,
                  };
                }
                return {
                  id: err?._id || `grammar-${pathKey}-${errIndex}`,
                  message: typeof err?.message === 'string' ? err.message : '',
                  shortMessage: err?.shortMessage,
                  ruleId: typeof err?.ruleId === 'string' ? err.ruleId : '',
                  category: typeof err?.category === 'string' ? err.category : '',
                  context: err?.context,
                  box,
                };
              });
              grammarEvaluation = {
                status: rawGrammar.status,
                errorCount: Number(rawGrammar.errorCount) || 0,
                wordCount: Number(rawGrammar.wordCount) || 0,
                fehlerindex: Number(rawGrammar.fehlerindex) || 0,
                errors: grammarErrors,
              };
            }

            let rawSubTasks: any[] = [];
            try {
              const maybeSubTasks = task?.subTasksSubmission;
              rawSubTasks = Array.isArray(maybeSubTasks) ? maybeSubTasks : [];
            } catch {
              rawSubTasks = [];
            }
            const mappedSubTasks = rawSubTasks.map((subTask: any, subTaskIndex: number) =>
              mapSubmissionTask(subTask, String(subTaskIndex + 1), fullPath),
            );

            return {
              _id: task?._id,
              taskNumber: normalizedTaskNumber,
              studentAnswer: task?.studentAnswer,
              gradingState: task?.gradingState,
              gradingResult: normalizeGradingResult(task?.gradingResult),
              boundingBoxes: mappedBoxes,
              grammarEvaluation,
              subTasksSubmission: mappedSubTasks,
            };
          };

          const mappedTasks = (sub.taskSubmissions || sub.tasksubmissions || []).map((mt: any, mainTaskIndex: number) =>
            mapSubmissionTask(mt, String(mainTaskIndex + 1), []),
          );

          return {
            _id: sub._id,
            studentId: sub.studentId,
            studentName:
              typeof sub.studentName === 'string' && sub.studentName.trim().length > 0
                ? sub.studentName.trim()
                : sub.studentId
                  ? `Student ${sub.studentId}`
                  : `Schüler ${index + 1}`,
            imageIds,
            pages,
            // Typed on the student exam page, so its boxes hold the student's own words rather than an
            // OCR reading of them — the grading canvas drops its extraction UI for these.
            answersSubmittedOnline: sub.answersSubmittedOnline === true,
            taskSubmissions: mappedTasks,
            languageUseRuleResults: sub.languageUseRuleResults,
          };
        }),
      );

      if (isMounted) setEnhancedSubmissions(enhanced);
    };

    buildEnhanced();

    return () => {
      isMounted = false;
    };
  }, [submissions]);

  const messages = conversation?.messages ?? [];

  const { isAgentThinkingRaw, agentThinkingText } = useMemo(() => {
    const splitState = (exam as any)?.examState?.splitConfirmationState;
    const taskExtractionProgress = (exam as any)?.examState?.taskExtractionProgress;

    const hasStructureConfirmationMessage = messages.some(
      m => m._class === ConfirmationMessage._class && (m as ConfirmationMessage).confirmationType === 'structure',
    );

    const splitStatusOk = splitState?.status === 'STARTED' || splitState?.status === 'RUNNING';
    const splitNotConfirmed = !splitState?.confirmed;
    const splitNoMessageId = !splitState?.messageId;
    const splitNotRequested = !(splitState?.message === 'Aufteilungsbestätigung angefordert');
    const isSplitAgentRunning = splitStatusOk && splitNotConfirmed && splitNoMessageId && splitNotRequested;

    // Mirrors the backend gate (`structureAnnounce`'s `ready()`, job-conditions.ts's
    // `splitConfirmedOrNotApplicable`): a split not yet confirmed also holds back showing structure as
    // "running", even once its own card has posted and `isSplitAgentRunning` above has gone false.
    const splitResolved = !splitState || splitState.confirmed === true;

    const isStructureAgentRunning =
      taskExtractionProgress?.status === 'IN_PROGRESS' &&
      !hasStructureConfirmationMessage &&
      !isSplitAgentRunning &&
      splitResolved;
    return {
      isAgentThinkingRaw: isSplitAgentRunning || isStructureAgentRunning,
      agentThinkingText: isSplitAgentRunning
        ? t('Chat.loading.splitThinkingMessage')
        : t('Chat.loading.structureThinkingMessage'),
    };
  }, [exam, messages]);

  const [isAgentThinking, setIsAgentThinking] = useState(false);

  useEffect(() => {
    const shouldShow = isAgentThinkingRaw && !isLoading && !conversation?.thinking;
    setIsAgentThinking(shouldShow);
  }, [isAgentThinkingRaw, isLoading, conversation?.thinking]);

  // Input must stay locked while a user turn stream is active OR server-side thinking is true.
  const isInputLocked = isLoading || isGenerateAnswerStreamingRef.current || Boolean(conversation?.thinking);

  const answerBbxUncertaintyCount = useMemo(() => {
    const examTasks = Array.isArray((exam as any)?.tasks) ? (exam as any).tasks : [];
    return findBbxUncertainties(enhancedSubmissions as any).filter(({ submissionIdx, mainTaskIdx, subTaskIdx }) => {
      const submission = (enhancedSubmissions as any)[submissionIdx];
      const mainTask = submission?.taskSubmissions?.[mainTaskIdx];
      if (!mainTask) return false;
      const mainTaskNumber = String((mainTask as any)?.taskNumber ?? '').trim();
      if (!mainTaskNumber) return false;
      let examNode = examTasks.find((et: any) => String(et?.taskNumber ?? '').trim() === mainTaskNumber);
      if (subTaskIdx === null) return examNode?.needsAnswer !== false;
      const entry = flattenSubTasksWithMeta(mainTask).find(e => e.flatIndex === subTaskIdx);
      if (!entry) return false;
      for (const seg of entry.numberPath) {
        const children = Array.isArray(examNode?.subTasks) ? examNode.subTasks : [];
        examNode = children.find((et: any) => String(et?.taskNumber ?? '').trim() === seg);
        if (examNode?.needsAnswer === false) return false;
      }
      return true;
    }).length;
  }, [enhancedSubmissions, exam]);

  useEffect(() => {
    // Track confirmed state on every render, not just while the canvas is open — gating the ref
    // update on isOpen meant the very first observation after opening (e.g. a tab regaining focus,
    // or a fast auto-confirm racing the canvas open) could already be `true`, which was
    // indistinguishable from "opened for review after already confirmed" and so never closed.
    // Tracking continuously from mount means `wasSplitConfirmed` reflects the real prior state.
    const splitState = (exam as any)?.examState?.splitConfirmationState;
    const isSplitConfirmed = Boolean(splitState?.confirmed || splitState?.status === 'DONE');
    const wasSplitConfirmed = previousSplitConfirmationRef.current;
    previousSplitConfirmationRef.current = isSplitConfirmed;

    if (wasSplitConfirmed === null || !isSplitConfirmed || wasSplitConfirmed) return;
    if (!canvasState.isOpen || canvasState.view !== 'submissioncheck') return;

    setCanvasState(prev => {
      if (!prev.isOpen || prev.view !== 'submissioncheck') return prev;
      return { isOpen: false, view: null };
    });
  }, [canvasState.isOpen, canvasState.view, exam, messages]);

  useEffect(() => {
    // See the split effect above for why this tracks continuously instead of only while open.
    const structureState = (exam as any)?.examState?.structureConfirmationState;
    const isStructureConfirmed = Boolean(structureState?.confirmed || structureState?.status === 'DONE');
    const wasStructureConfirmed = previousStructureConfirmationRef.current;
    previousStructureConfirmationRef.current = isStructureConfirmed;

    if (wasStructureConfirmed === null || !isStructureConfirmed || wasStructureConfirmed) return;
    if (!canvasState.isOpen || canvasState.view !== 'structureeditor') return;

    setCanvasState(prev => {
      if (!prev.isOpen || prev.view !== 'structureeditor') return prev;
      return { isOpen: false, view: null };
    });
  }, [canvasState.isOpen, canvasState.view, exam, messages]);

  useEffect(() => {
    const pending = (conversation as any)?.pendingCanvasOpen;
    if (!pending) return;
    const validTypes = [
      'boundingbox',
      'correction',
      'answers',
      'msedit',
      'summary',
      'submissioncheck',
      'structureeditor',
      'demoselector',
      'examcreator',
    ] as const;
    if ((validTypes as readonly string[]).includes(pending)) {
      // "answers" is not a standalone canvas view: it opens the correction canvas in answer-review mode.
      if (pending === 'answers') {
        openCanvas('correction', { mode: 'answers' });
      } else {
        openCanvas(pending as Exclude<(typeof validTypes)[number], 'answers'>);
      }
      // Deliberately NOT cleared here — see the close-triggered effect below. Clearing the instant a
      // subscription observes the flag conflates "some client happened to be listening" with "the
      // teacher actually saw it": a background tab (or one on a different exam) would silently consume
      // it before anyone looks, and a fresh reload of this exam would then never reopen the canvas.
    }
  }, [(conversation as any)?.pendingCanvasOpen]);

  // Tracks which view a pending-triggered canvas open corresponds to, so the clear-on-close effect below
  // knows which flag to clear even after canvasState.view has been reset to null by the close itself.
  const lastOpenCanvasViewRef = useRef<typeof canvasState.view>(null);
  useEffect(() => {
    if (canvasState.isOpen && canvasState.view) {
      lastOpenCanvasViewRef.current = canvasState.view;
    }
  }, [canvasState.isOpen, canvasState.view]);

  // Clears pendingCanvasOpen once the teacher actually closes the canvas it asked to open — not the
  // instant some subscription merely observes the flag (see the open effect above). This is what makes
  // the signal survive an exam switch or a fresh reload: it stays set until someone genuinely sees it.
  //
  // Must fire on a genuine TRUE→FALSE transition of canvasState.isOpen, not merely "isOpen is currently
  // false": right after switching exams, isOpen is reset to false in the SAME render where the returning
  // subscription can deliver an already-set pendingCanvasOpen — but openCanvas()'s own setCanvasState call
  // (from the open effect above) only takes effect on the NEXT render. A plain "if (!isOpen)" check can't
  // tell that apart from an actual close, and would clear the flag a render before the canvas even opens.
  const prevCanvasIsOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevCanvasIsOpenRef.current;
    prevCanvasIsOpenRef.current = canvasState.isOpen;
    if (!wasOpen || canvasState.isOpen) return;

    const closedView = lastOpenCanvasViewRef.current;
    if (!closedView) return;
    const pending = (conversation as any)?.pendingCanvasOpen;
    if (!pending) return;
    // "answers" opens the "correction" view in answer-review mode — see the open effect above.
    const expectedView = pending === 'answers' ? 'correction' : pending;
    if (expectedView !== closedView) return;
    lastOpenCanvasViewRef.current = null;
    if (conversationId !== 'new') {
      clearPendingCanvasOpenMutation(conversationId)().catch(err =>
        logger.error(err, `Failed to clear pendingCanvasOpen`),
      );
    }
  }, [canvasState.isOpen, (conversation as any)?.pendingCanvasOpen, conversationId]);

  useEffect(() => {
    const fileId = (conversation as any)?.pendingDownload as string | undefined;
    if (!fileId) return;

    void (async () => {
      try {
        const file = await getFileByIDQuery(fileId)({ _id: 1, meta: { fileName: 1, extension: 1 } }).catch(() => null);
        if (!file) {
          logger.error(`pendingDownload: Datei ${fileId} nicht gefunden`);
          return;
        }
        const downloadUrl = file.getDownloadLink();
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) {
          window.location.href = downloadUrl;
        } else {
          const fileName = file.meta?.fileName
            ? `${file.meta.fileName}${file.meta.extension ? `.${file.meta.extension}` : ''}`
            : 'download.md';
          const a = document.createElement('a');
          a.href = downloadUrl;
          a.download = fileName;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      } finally {
        if (conversationId !== 'new') {
          clearPendingDownloadMutation(conversationId)().catch(err =>
            logger.error(err, 'Failed to clear pendingDownload'),
          );
        }
      }
    })();
  }, [(conversation as any)?.pendingDownload]);

  const ensureConversationAndExamContext = async (): Promise<{ conversationId: string; examId: string } | null> => {
    if (conversationId !== 'new' && examId) {
      // Validate that the cached exam still belongs to the current user context.
      // After login/logout, React state might not have reset yet (race condition), so we
      // compare the conversation's owner against the current effective user ID.
      const effectiveUserId = (user as any)?._id ?? getGuestUserID();
      if (!conversation?.userID || conversation.userID === effectiveUserId) {
        return { conversationId, examId };
      }
      // Owner mismatch — fall through to create a new exam for the current session
    }

    if (conversationId !== 'new' && !examId) {
      try {
        const existingConversation = await getByIDAny(Conversation, conversationId)({ _id: 1, examId: 1, userID: 1 });
        const existingUserId = String(existingConversation?.userID || '');
        if (user && existingUserId && existingUserId !== (user as any)?._id) {
          logger.warn(`Ignoring conversationId that does not belong to current user: ${conversationId}`);
        } else {
          const existingExamId = String(existingConversation?.examId || '');
          if (existingExamId) {
            return { conversationId, examId: existingExamId };
          }
        }
      } catch (err) {
        logger.error(err, `Error loading examId from existing conversation: ${conversationId}`);
      }
    }

    try {
      const newConversation = await createExamMutation(
        i18n.language || 'de',
        getGuestUserID(),
      )({
        _id: 1,
        examId: 1,
      });

      const nextConversationId = String(newConversation?._id || '');
      const nextExamId = String(newConversation?.examId || '');

      if (!nextConversationId || !nextExamId) {
        throw new Error('createExamMutation returned no conversationId/examId');
      }

      if (isGuest) {
        setGuestConversationId(nextConversationId);
      }

      setConversationId(nextConversationId);
      onConversationIdChange?.(nextConversationId);
      setIsLoading(false);
      triggerExamUpdate();

      return { conversationId: nextConversationId, examId: nextExamId };
    } catch (err) {
      logger.error(err, `Error creating exam:`);
      setIsLoading(false);
      return null;
    }
  };

  // Tells the agent what's currently on screen — purely informational (see prompts.ts). MS/GS edits
  // still require the teacher to name the task number explicitly, regardless of this context.
  const buildActiveCanvasContext = () => {
    const isAnswersMode = canvasState.view === 'correction' && canvasState.data?.mode === 'answers';
    // "answers" isn't a canvas view of its own — it's the correction canvas in review-only mode — but
    // the agent's own open_canvas tool distinguishes them, so mirror that vocabulary here too.
    const view = isAnswersMode ? 'answers' : canvasState.view || 'none';
    const visibleTaskNumber = view === 'msedit' ? selectedTaskNumber || null : visibleCorrectionTask.taskNumber;
    const visibleStudentId =
      view === 'msedit' ? selectedStudentId || null : visibleCorrectionTask.studentId || selectedStudentId || null;
    const visibleStudentName = view === 'correction' || view === 'answers' ? visibleCorrectionTask.studentName : null;
    return JSON.stringify({
      isOpen: canvasState.isOpen,
      view,
      selectedTaskNumber: visibleTaskNumber,
      selectedStudentId: visibleStudentId,
      selectedStudentName: visibleStudentName,
    });
  };

  const handleSubmit = async (userMessage: string, opts?: { attachedFileNames?: string[] | null }) => {
    const attachedFileNames = Array.isArray(opts?.attachedFileNames)
      ? opts.attachedFileNames.filter(name => typeof name === 'string' && name.trim().length > 0)
      : [];
    if (!userMessage.trim() && attachedFileNames.length === 0) return;

    setIsLoading(true);

    const context = await ensureConversationAndExamContext();
    if (!context) return;
    const targetId = context.conversationId;

    const activeCanvasContext = buildActiveCanvasContext();

    const attachedToken = attachedFileNames.map(name => `[[ATTACHMENT:${name}]]`).join(' ');
    const payloadMessage = [userMessage.trim(), attachedToken].filter(Boolean).join(' ');

    await finalizeAssignmentDrafts(targetId);

    const currentLanguageName = languageMap[i18n.language] || 'Deutsch';

    logger.info(currentLanguageName);

    const capturedConversationId = targetId;
    isGenerateAnswerStreamingRef.current = true;

    generateAnswer(
      targetId,
      payloadMessage,
      activeCanvasContext,
      false,
      getGuestUserID(),
      currentLanguageName,
    )(convSelectionSet).subscribe({
      next: conversation => {
        if (activeConversationIdRef.current !== capturedConversationId) return;
        if (isLoading) setIsLoading(false);
        setConversation(prev => {
          const next = conversation.toObject();
          if (!prev) return new Conversation(next);
          const prevObj = (prev as Conversation).toObject();
          const merged = { ...prevObj, ...next, messages: next.messages ?? prevObj.messages };
          return new Conversation(merged as any);
        });
      },
      error: err => {
        isGenerateAnswerStreamingRef.current = false;
        if (activeConversationIdRef.current !== capturedConversationId) {
          logger.error(err, `Error sending message (inactive conversation): ${capturedConversationId}`);
          return;
        }
        logger.error(err, `Error sending message: ${capturedConversationId}`);
        setError(err);
        setIsLoading(false);
      },
      complete: () => {
        isGenerateAnswerStreamingRef.current = false;
        if (activeConversationIdRef.current !== capturedConversationId) {
          triggerExamUpdate();
          return;
        }
        setIsLoading(false);
        onMessageSent?.(capturedConversationId);
        void fetchExamData();
        fetchSubmissions();
        if (canvasState.isOpen && examId && canvasState.view === 'correction') {
          void refreshTaskEditData();
        }
        triggerExamUpdate();
      },
    });
  };

  const handleEditMessage = async (messageId: string, newContent: string) => {
    if (!newContent.trim()) return;

    setIsLoading(true);

    const activeCanvasContext = buildActiveCanvasContext();

    await finalizeAssignmentDrafts(conversationId);

    const currentLanguageName = languageMap[i18n.language] || 'Deutsch';

    const capturedConversationId = conversationId;
    isGenerateAnswerStreamingRef.current = true;

    generateAnswer(
      conversationId,
      newContent,
      activeCanvasContext,
      true,
      getGuestUserID(),
      currentLanguageName,
    )(convSelectionSet).subscribe({
      next: conversation => {
        if (activeConversationIdRef.current !== capturedConversationId) return;
        setConversation(prev => {
          const next = conversation.toObject();
          if (!prev) return new Conversation(next);
          const prevObj = (prev as Conversation).toObject();
          const merged = { ...prevObj, ...next, messages: next.messages ?? prevObj.messages };
          return new Conversation(merged as any);
        });
      },
      error: err => {
        isGenerateAnswerStreamingRef.current = false;
        if (activeConversationIdRef.current !== capturedConversationId) {
          logger.error(err, `Error editing message (inactive conversation): ${capturedConversationId}`);
          return;
        }
        logger.error(err, `Error editing message: ${capturedConversationId}`);
        setError(err);
        setIsLoading(false);
      },
      complete: () => {
        isGenerateAnswerStreamingRef.current = false;
        if (activeConversationIdRef.current !== capturedConversationId) {
          triggerExamUpdate();
          return;
        }
        setIsLoading(false);
        void fetchExamData();
        fetchSubmissions();
        if (canvasState.isOpen && examId && canvasState.view === 'correction') {
          void refreshTaskEditData();
        }
        triggerExamUpdate();
      },
    });
  };

  const handleSaveSubmissionTask = async (
    submissionId: string,
    mainTaskId: string,
    subTaskId: string | null,
    boxId: string | null,
    updatedData: any,
  ) => {
    try {
      // A shared LanguageUseScheme group's result is keyed by submissionId + languageUseSchemeId, not
      // a task — routes to its own override mutation, no bounding-box/content logic applies.
      if (updatedData.target?.kind === 'group') {
        const languageUseSchemeId = updatedData.target.languageUseSchemeId;
        await updateLanguageUseRuleResultMutation(
          submissionId,
          languageUseSchemeId,
          updatedData.languageUseScore as any,
          updatedData.languageUseExplanation as any,
          updatedData.languageUseIsApproved as any,
          updatedData.languageUseScore as any,
          updatedData.languageUseReviewerFeedback as any,
        )();

        setEnhancedSubmissions(prev =>
          prev.map(sub => {
            if (sub._id !== submissionId) return sub;
            const existingResults: any[] = Array.isArray((sub as any).languageUseRuleResults)
              ? (sub as any).languageUseRuleResults
              : [];
            const idx = existingResults.findIndex(r => String(r.languageUseSchemeId) === String(languageUseSchemeId));
            const updatedResult = {
              ...(idx >= 0 ? existingResults[idx] : { languageUseSchemeId }),
              ...(updatedData.languageUseScore !== undefined
                ? { score: updatedData.languageUseScore, actualPointsGiven: updatedData.languageUseScore }
                : {}),
              ...(updatedData.languageUseExplanation !== undefined
                ? { explanation: updatedData.languageUseExplanation }
                : {}),
              ...(updatedData.languageUseIsApproved !== undefined
                ? { isApproved: updatedData.languageUseIsApproved }
                : {}),
            };
            const nextResults =
              idx >= 0
                ? existingResults.map((r, i) => (i === idx ? updatedResult : r))
                : [...existingResults, updatedResult];
            return { ...sub, languageUseRuleResults: nextResults };
          }),
        );
        return;
      }

      let boundingBoxIndex = 0;
      if (boxId) {
        if (boxId.startsWith('box-new-')) {
          boundingBoxIndex = -1;
        } else {
          const parts = boxId.split('-');
          const indexToken = parts[parts.length - 1] ?? '0';
          const parsed = parseInt(indexToken, 10);
          if (!isNaN(parsed)) boundingBoxIndex = parsed;
        }
      }

      if (updatedData.boxCoordinates) {
        logger.info(
          `API-Aufruf für neue Koordinaten noch implementieren: ${JSON.stringify(updatedData.boxCoordinates)}`,
        );
      }

      if (
        updatedData.extractedText !== undefined ||
        updatedData.score !== undefined ||
        updatedData.gradingText !== undefined ||
        updatedData.boxCoordinates !== undefined ||
        updatedData.addBoundingBox === true ||
        updatedData.deleteBoundingBox === true ||
        updatedData.isApproved !== undefined ||
        updatedData.boxApproved !== undefined ||
        updatedData.rerunOcr === true
      ) {
        const result = await updateSubmissionTaskMutation(
          submissionId,
          mainTaskId,
          subTaskId || null,
          boundingBoxIndex,
          updatedData.extractedText as any,
          updatedData.score as any,
          updatedData.gradingText as any,
          updatedData.maxScore as any,
          updatedData.boxCoordinates?.x as any,
          updatedData.boxCoordinates?.y as any,
          updatedData.boxCoordinates?.width as any,
          updatedData.boxCoordinates?.height as any,
          updatedData.boxCoordinates?.pageNumber as any,
          updatedData.addBoundingBox as any,
          updatedData.deleteBoundingBox as any,
          updatedData.isApproved as any,
          updatedData.boxApproved as any,
          undefined as any, // reviewerFeedback (not set from this path)
          updatedData.rerunOcr as any,
        )({ success: 1, message: 1 });

        logger.info(`[handleSaveSubmissionTask] mutation result: ${JSON.stringify(result)}`);
        if (result && !(result as any).success) {
          logger.error(`[handleSaveSubmissionTask] DB update failed: ${JSON.stringify((result as any).message)}`);
        }
      }

      setEnhancedSubmissions(prev =>
        prev.map(sub => {
          if (sub._id !== submissionId) return sub;

          return {
            ...sub,
            taskSubmissions: (sub.taskSubmissions || []).map((mainTask: any) => {
              if (mainTask._id !== mainTaskId) return mainTask;

              const isGradingUpdate =
                updatedData.score !== undefined ||
                updatedData.gradingText !== undefined ||
                updatedData.isApproved !== undefined;

              if (!subTaskId) {
                const approveAllBoxes = updatedData.isApproved === true;
                return {
                  ...mainTask,
                  gradingResult: isGradingUpdate
                    ? {
                        ...(mainTask.gradingResult || {}),
                        ...(updatedData.score !== undefined
                          ? { score: updatedData.score, actualPointsGiven: updatedData.score }
                          : {}),
                        ...(updatedData.gradingText !== undefined ? { explanation: updatedData.gradingText } : {}),
                        ...(updatedData.isApproved !== undefined ? { isApproved: updatedData.isApproved } : {}),
                      }
                    : mainTask.gradingResult,
                  boundingBoxes: approveAllBoxes
                    ? (mainTask.boundingBoxes || []).map((box: any) => ({ ...box, approved: true }))
                    : updatedData.boxApproved !== undefined && boxId
                      ? (mainTask.boundingBoxes || []).map((box: any, idx: number) => {
                          const isTarget = boxId === `box-${idx}` || box.id === boxId;
                          return isTarget ? { ...box, approved: updatedData.boxApproved } : box;
                        })
                      : mainTask.boundingBoxes,
                };
              }

              return {
                ...mainTask,
                subTasksSubmission: (() => {
                  try {
                    const maybe = (mainTask as any)?.subTasksSubmission;
                    const list = Array.isArray(maybe) ? maybe : [];
                    return list.map((subTask: any) => {
                      if (subTask._id !== subTaskId) return subTask;
                      const approveAllBoxes = updatedData.isApproved === true;
                      return {
                        ...subTask,
                        gradingResult: isGradingUpdate
                          ? {
                              ...(subTask.gradingResult || {}),
                              ...(updatedData.score !== undefined
                                ? { score: updatedData.score, actualPointsGiven: updatedData.score }
                                : {}),
                              ...(updatedData.gradingText !== undefined
                                ? { explanation: updatedData.gradingText }
                                : {}),
                              ...(updatedData.isApproved !== undefined ? { isApproved: updatedData.isApproved } : {}),
                            }
                          : subTask.gradingResult,
                        boundingBoxes: approveAllBoxes
                          ? (subTask.boundingBoxes || []).map((box: any) => ({ ...box, approved: true }))
                          : updatedData.boxApproved !== undefined && boxId
                            ? (subTask.boundingBoxes || []).map((box: any, idx: number) => {
                                const isTarget = boxId === `box-${idx}` || box.id === boxId;
                                return isTarget ? { ...box, approved: updatedData.boxApproved } : box;
                              })
                            : subTask.boundingBoxes,
                      };
                    });
                  } catch {
                    return [];
                  }
                })(),
              };
            }),
          };
        }),
      );
      if (
        updatedData.addBoundingBox === true ||
        updatedData.deleteBoundingBox === true ||
        updatedData.boxCoordinates !== undefined ||
        updatedData.extractedText !== undefined
      ) {
        fetchSubmissions();
      }
    } catch (error) {
      logger.error(error, `Fehler beim Speichern:`);
      setError(error as Error);
    }
  };

  const handleDeleteGrammarError = async (submissionId: string, mainTaskId: string, errorId: string) => {
    try {
      await deleteGrammarErrorMutation(submissionId, mainTaskId, errorId)();
      setEnhancedSubmissions(prev =>
        prev.map(sub => {
          if (sub._id !== submissionId) return sub;
          return {
            ...sub,
            taskSubmissions: (sub.taskSubmissions || []).map((mainTask: any) => {
              if (mainTask._id !== mainTaskId) return mainTask;
              const errors = (mainTask.grammarEvaluation?.errors ?? []).filter((e: any) => e.id !== errorId);
              const wordCount = mainTask.grammarEvaluation?.wordCount ?? 0;
              return {
                ...mainTask,
                grammarEvaluation: {
                  ...mainTask.grammarEvaluation,
                  errors,
                  errorCount: errors.length,
                  fehlerindex: wordCount > 0 ? (errors.length / wordCount) * 100 : 0,
                },
              };
            }),
          };
        }),
      );
    } catch (err) {
      logger.error(err, 'handleDeleteGrammarError failed');
    }
  };

  const handleAddGrammarError = async (
    submissionId: string,
    mainTaskId: string,
    error: ManualGrammarError,
  ): Promise<boolean> => {
    try {
      const result = await addGrammarErrorMutation(
        submissionId,
        mainTaskId,
        error._id,
        error.category,
        'MANUAL',
        error.message,
        error.box.x,
        error.box.y,
        error.box.width,
        error.box.height,
        error.box.pageNumber,
      )();
      if (!result) return false;
      setEnhancedSubmissions(prev =>
        prev.map(sub => {
          if (sub._id !== submissionId) return sub;
          return {
            ...sub,
            taskSubmissions: (sub.taskSubmissions || []).map((mainTask: any) => {
              if (mainTask._id !== mainTaskId) return mainTask;
              const errors = [
                ...(mainTask.grammarEvaluation?.errors ?? []),
                {
                  id: error._id,
                  message: error.message,
                  ruleId: 'MANUAL',
                  category: error.category,
                  box: error.box,
                  isManual: true,
                },
              ];
              const wordCount = mainTask.grammarEvaluation?.wordCount ?? 0;
              return {
                ...mainTask,
                grammarEvaluation: {
                  ...mainTask.grammarEvaluation,
                  errors,
                  errorCount: errors.length,
                  fehlerindex: wordCount > 0 ? (errors.length / wordCount) * 100 : 0,
                },
              };
            }),
          };
        }),
      );
      return true;
    } catch (err) {
      logger.error(err, 'handleAddGrammarError failed');
      return false;
    }
  };

  const handleUpdateGrammarError = async (
    submissionId: string,
    mainTaskId: string,
    errorId: string,
    category: string,
    message: string,
    box: { x: number; y: number; width: number; height: number; pageNumber: number },
  ): Promise<boolean> => {
    try {
      const result = await updateGrammarErrorMutation(
        submissionId,
        mainTaskId,
        errorId,
        category,
        message,
        box.x,
        box.y,
        box.width,
        box.height,
        box.pageNumber,
      )();
      if (!result) return false;
      setEnhancedSubmissions(prev =>
        prev.map(sub => {
          if (sub._id !== submissionId) return sub;
          return {
            ...sub,
            taskSubmissions: (sub.taskSubmissions || []).map((mainTask: any) => {
              if (mainTask._id !== mainTaskId) return mainTask;
              return {
                ...mainTask,
                grammarEvaluation: {
                  ...mainTask.grammarEvaluation,
                  errors: (mainTask.grammarEvaluation?.errors ?? []).map((e: any) => {
                    if (e.id !== errorId) return e;
                    return { ...e, category, message, box };
                  }),
                },
              };
            }),
          };
        }),
      );
      return true;
    } catch (err) {
      logger.error(err, 'handleUpdateGrammarError failed');
      return false;
    }
  };

  const handleCreateNewSubmission = useCallback(
    async (studentName: string, imageIds: string[]): Promise<string> => {
      if (!examId) {
        throw new Error('Exam ID nicht verfügbar');
      }
      const result = await createSubmissionMutation(
        examId,
        imageIds,
        studentName,
        0,
        [],
        getGuestUserID(),
      )({
        _id: 1,
      });
      // Unlike the split flow (submission-split.ts), which counts its whole batch in one
      // `updateSubmissionsFoundMutation` call, this manual add is the one path that creates a submission
      // without ever touching that counter — see exam-lock.ts's own note on why `isExamCreatorLocked`
      // does not rely on it alone, but it still has to be right for `hasSubmissions` (job-conditions.ts),
      // which the exam-creation job's `ready()` gates on directly.
      await updateSubmissionsFoundMutation(examId, 1)().catch(err => {
        logger.error(err, 'Failed to record the manually added submission in uploadProgress.submissionsFound');
      });
      return (result as any)?._id || '';
    },
    [examId],
  );

  const handleDeleteSubmission = useCallback(
    async (submissionId: string): Promise<void> => {
      await deleteSubmissionMutation(submissionId)();
      fetchSubmissions();
      triggerExamUpdate();
    },
    [fetchSubmissions],
  );

  const handleSaveSubmissionReview = async (
    reassignment: { submissionId: string; imageIds: string[]; studentName?: string }[],
  ) => {
    const flattenedReassigned = reassignment.flatMap(item => item.imageIds);
    const flattenedCurrent = submissions.flatMap(sub => {
      const imageIds = (sub as any)?.imageIds;
      return Array.isArray(imageIds) ? imageIds : [];
    });

    const countById = (ids: string[]) => {
      const map = new Map<string, number>();
      ids.forEach(id => {
        map.set(id, (map.get(id) || 0) + 1);
      });
      return map;
    };

    const reassignedCount = countById(flattenedReassigned);
    const currentCount = countById(flattenedCurrent);

    if (reassignedCount.size !== currentCount.size) {
      throw new Error('Ungültige Neuverteilung: Seitenzuordnung ist unvollständig oder enthält unbekannte Seiten.');
    }

    for (const [imageId, count] of currentCount) {
      if (reassignedCount.get(imageId) !== count) {
        throw new Error('Ungültige Neuverteilung: Seiten wurden doppelt zugeordnet oder fehlen.');
      }
    }

    const updates = reassignment.map(item =>
      updateSubmissionImageIdsMutation(
        item.submissionId,
        item.imageIds,
        item.studentName || '',
      )({
        success: 1,
        message: 1,
      }),
    );

    const results = await Promise.all(updates);
    const failed = results.find(result => !(result as any)?.success);
    if (failed) {
      throw new Error((failed as any)?.message || 'Fehler beim Speichern der Submission-Zuordnung.');
    }

    fetchSubmissions();
    triggerExamUpdate();
  };

  const renderCanvasView = () => {
    const structureState = (exam as any)?.examState?.structureConfirmationState;
    const isStructureReadOnly =
      STRUCTURE_READONLY_ON_CONFIRM &&
      (Boolean(structureState?.confirmed || structureState?.status === 'DONE') ||
        Boolean(structureState?.message === 'Bestätigung der Klausurstruktur ist abgelaufen'));

    // Same trigger the server enforces identically (see db/utils/exam-lock.ts's `isExamCreatorLocked`,
    // shared by exam-creation.job.ts's `ready()`, create-exam.tool.ts and exam-editor-api.ts's mutations):
    // a master solution or grading scheme already uploaded/generated, student submissions already in, or
    // any answer already extracted. Computed inline from already-fetched exam fields rather than importing
    // that server module, same as `isStructureReadOnly` above does for the structure editor's own gate.
    // `answerExtractionProgress` is checked alongside `uploadProgress.submissionsFound`, not instead of it
    // — see exam-lock.ts's own note on why the counter alone can under-report.
    const msProgressStatus = (exam as any)?.examState?.masterSolutionProgress?.status;
    const gsProgressStatus = (exam as any)?.examState?.gradingSchemeProgress?.status;
    const answerExtractionStatus = (exam as any)?.examState?.answerExtractionProgress?.status;
    const isExamCreatorReadOnly = Boolean(
      ((exam as any)?.masterSolutionIds?.length ?? 0) > 0 ||
      ((exam as any)?.gradingSchemeIds?.length ?? 0) > 0 ||
      msProgressStatus === 'IN_PROGRESS' ||
      msProgressStatus === 'COMPLETED' ||
      gsProgressStatus === 'IN_PROGRESS' ||
      gsProgressStatus === 'COMPLETED' ||
      Number((exam as any)?.examState?.uploadProgress?.submissionsFound ?? 0) > 0 ||
      answerExtractionStatus === 'IN_PROGRESS' ||
      answerExtractionStatus === 'COMPLETED' ||
      ((exam as any)?.examState?.answerExtractionProgress?.finishedUnits?.length ?? 0) > 0,
    );

    switch (canvasState.view) {
      case 'msedit':
        if (isMsLoading) {
          return (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4">
              <div className="w-8 h-8 border-4 border-border border-t-brand-strong rounded-full animate-spin" />
              <span className="text-sm font-medium">{t('Chat.loading.masterSolutions')}</span>
            </div>
          );
        }

        const currentIndex = Math.max(
          0,
          tasks.findIndex(t => t.id === selectedTaskNumber),
        );

        return (
          <SolutionGradingCheck
            examId={examId || ''}
            pages={pageUrls}
            isGradingInProgress={isGradingInProgress}
            refiningMasterSolutionTaskIds={refiningMasterSolutionTaskIds}
            refiningGradingSchemeTaskIds={refiningGradingSchemeTaskIds}
            refiningGradingSchemeLanguageUseTaskIds={refiningGradingSchemeLanguageUseTaskIds}
            tasks={tasks.map(task => ({
              id: task.id,
              name: task.name,
              taskNumber: task.taskNumber,
              taskType: taskdata[task.id]?.taskType ?? (task as any).taskType,
              extractedText: taskdata[task.id]?.extractedText || '',
              imageIds: taskdata[task.id]?.imageIds || [],
              descriptionBoxesJson: taskdata[task.id]?.descriptionBoxesJson ?? null,
              masterSolutionImageIds: taskdata[task.id]?.masterSolutionImageIds || [],
              gradingSchemeImageIds: taskdata[task.id]?.gradingSchemeImageIds || [],
              masterSolutionConfidence:
                taskdata[task.id]?.masterSolutionConfidence ?? (task as any).masterSolutionConfidence,
              gradingSchemeConfidence:
                taskdata[task.id]?.gradingSchemeConfidence ?? (task as any).gradingSchemeConfidence,
              masterSolutionIsApproved:
                taskdata[task.id]?.masterSolutionIsApproved ?? (task as any).masterSolutionIsApproved,
              gradingSchemeIsApproved:
                taskdata[task.id]?.gradingSchemeIsApproved ?? (task as any).gradingSchemeIsApproved,
              // Provenance per task — drives the "KI-generiert" badge and whether confidence is coloured.
              // Both panels read it off the task, so leaving it out here silently disabled the badge even
              // though the DTO, the query selection and the entity all carried it.
              masterSolutionSource: taskdata[task.id]?.masterSolutionSource ?? (task as any).masterSolutionSource,
              gradingSchemeSource: taskdata[task.id]?.gradingSchemeSource ?? (task as any).gradingSchemeSource,
              masterSolution: taskdata[task.id]?.masterSolution || '',
              gradingCriteria: taskdata[task.id]?.gradingCriteria || '',
              gradingMaxPoints: taskdata[task.id]?.gradingMaxPoints ?? 0,
              descriptionContentType: taskdata[task.id]?.descriptionContentType,
              masterSolutionContentType: taskdata[task.id]?.masterSolutionContentType,
              gradingSchemeContentType: taskdata[task.id]?.gradingSchemeContentType,
              needsAnswer: taskdata[task.id]?.needsAnswer ?? (task as any).needsAnswer ?? true,
              languageUseGroupsJson: taskdata[task.id]?.languageUseGroupsJson ?? null,
            }))}
            currentTaskIndex={currentIndex}
            onTaskChange={newIndex => {
              const selectedTask = tasks[newIndex];
              if (selectedTask) {
                setSelectedTaskNumber(selectedTask.id);
              }
            }}
            onUpdateMasterSolution={(index, newSolution) => {
              const targetTask = tasks[index];
              if (!targetTask || !examId) return;

              const dataKey = targetTask.id;

              setTaskData(prev => ({
                ...prev,
                [dataKey]: { ...prev[dataKey], masterSolution: newSolution, masterSolutionIsApproved: true },
              }));

              void updateExamTaskMasterSolutionMutation(examId, dataKey, newSolution)()
                .then(() => approveMsSectionMutation(examId, dataKey, 'ms')())
                .then(() => {
                  triggerExamUpdate();
                })
                .catch(err => {
                  logger.error(err, `Error saving master solution:`);
                });
            }}
            onUpdateGradingScheme={(index, grading, target) => {
              const targetTask = tasks[index];
              if (!targetTask || !examId) return;

              const dataKey = targetTask.id;

              if (target?.kind === 'group') {
                // A content save auto-approves (see the plain-content branch below) — a group save
                // must too, or saving a LanguageUseScheme (solo or multi-member — every language-use
                // rule is one now) silently leaves it unapproved while every other save path flips
                // isApproved. updateLanguageUseSchemeMutation already supports this (see its own
                // isApproved param doc); only this call site never passed it.
                setTaskData(prev => ({
                  ...prev,
                  [dataKey]: {
                    ...prev[dataKey],
                    languageUseGroupsJson: patchLanguageUseGroupsJson(
                      prev[dataKey]?.languageUseGroupsJson,
                      target.languageUseSchemeId,
                      {
                        criteria: grading.criteria,
                        maxPoints: grading.maxPoints,
                        isApproved: true,
                      },
                    ),
                  },
                }));

                void updateLanguageUseSchemeMutation(
                  examId,
                  target.languageUseSchemeId,
                  grading.criteria,
                  grading.maxPoints,
                  true,
                )()
                  .then(() => {
                    triggerExamUpdate();
                  })
                  .catch(err => {
                    logger.error(err, `Error saving language-use group scheme:`);
                  });
                return;
              }

              setTaskData(prev => ({
                ...prev,
                [dataKey]: {
                  ...prev[dataKey],
                  gradingCriteria: grading.criteria,
                  gradingMaxPoints: grading.maxPoints,
                  gradingSchemeIsApproved: true,
                },
              }));

              void updateExamTaskGradingSchemeMutation(examId, dataKey, grading.criteria, grading.maxPoints)()
                .then(() => approveMsSectionMutation(examId, dataKey, 'gs')())
                .then(() => {
                  triggerExamUpdate();
                })
                .catch(err => {
                  logger.error(err, `Error saving grading scheme:`);
                });
            }}
            onApproveMasterSolution={index => {
              const targetTask = tasks[index];
              if (!targetTask || !examId) return;

              const dataKey = targetTask.id;

              setTaskData(prev => ({
                ...prev,
                [dataKey]: { ...prev[dataKey], masterSolutionIsApproved: true },
              }));

              void approveMsSectionMutation(examId, dataKey, 'ms')().catch(err => {
                logger.error(err, `Error approving master solution:`);
              });
            }}
            onApproveGradingScheme={(index, target) => {
              const targetTask = tasks[index];
              if (!targetTask || !examId) return;

              const dataKey = targetTask.id;

              if (target?.kind === 'group') {
                // LanguageUseScheme groups have no dedicated approve mutation (unlike own-criteria's
                // approveMsSectionMutation) — reuse updateLanguageUseSchemeMutation with the group's own
                // current criteria/maxPoints unchanged plus isApproved:true (see final report).
                let groups: any[] = [];
                try {
                  const parsed = taskdata[dataKey]?.languageUseGroupsJson
                    ? JSON.parse(taskdata[dataKey].languageUseGroupsJson)
                    : [];
                  groups = Array.isArray(parsed) ? parsed : [];
                } catch {
                  groups = [];
                }
                const groupInfo = groups.find(group => group?._id === target.languageUseSchemeId);
                if (!groupInfo?._id) return;

                setTaskData(prev => ({
                  ...prev,
                  [dataKey]: {
                    ...prev[dataKey],
                    languageUseGroupsJson: patchLanguageUseGroupsJson(
                      prev[dataKey]?.languageUseGroupsJson,
                      target.languageUseSchemeId,
                      {
                        isApproved: true,
                      },
                    ),
                  },
                }));

                void updateLanguageUseSchemeMutation(
                  examId,
                  groupInfo._id,
                  groupInfo.criteria,
                  groupInfo.maxPoints,
                  true,
                )().catch(err => {
                  logger.error(err, `Error approving language-use group scheme:`);
                });
                return;
              }

              setTaskData(prev => ({
                ...prev,
                [dataKey]: { ...prev[dataKey], gradingSchemeIsApproved: true },
              }));

              void approveMsSectionMutation(examId, dataKey, 'gs')().catch(err => {
                logger.error(err, `Error approving grading scheme:`);
              });
            }}
            onDeleteSolutionImage={(index, imageId, type) => {
              const targetTask = tasks[index];
              if (!targetTask || !examId || !imageId) return;

              const dataKey = targetTask.id;
              const idsKey = type === 'masterSolution' ? 'masterSolutionImageIds' : 'gradingSchemeImageIds';

              // Optimistically drop the imageId from the cached task data so the deletion sticks across
              // navigation and survives refreshMsEditData's "keep old ids when the new array is empty"
              // fallback (which would otherwise resurrect the last deleted image).
              setTaskData(prev => {
                const existing: string[] = prev[dataKey]?.[idsKey] || [];
                return {
                  ...prev,
                  [dataKey]: { ...prev[dataKey], [idsKey]: existing.filter(id => id !== imageId) },
                };
              });

              void deleteBoundingBoxbyImageIdMutation(imageId, examId, dataKey, type)().catch(err => {
                logger.error(err, `Error deleting solution image:`);
              });
            }}
            onAddSolutionImage={(index, imageId, type) => {
              const targetTask = tasks[index];
              if (!targetTask || !examId || !imageId) return;

              const dataKey = targetTask.id;
              const idsKey = type === 'masterSolution' ? 'masterSolutionImageIds' : 'gradingSchemeImageIds';

              // Optimistically append the imageId to the cached task data so it persists across
              // navigation and matches what the optimistic resolved-image list in the panel shows.
              setTaskData(prev => {
                const existing: string[] = prev[dataKey]?.[idsKey] || [];
                return {
                  ...prev,
                  [dataKey]: { ...prev[dataKey], [idsKey]: [...existing, imageId] },
                };
              });

              void addBoundingBoxByImageIdMutation(imageId, examId, dataKey, type)().catch(err => {
                logger.error(err, `Error adding solution image:`);
              });
            }}
            onUpdateLanguageUseGroupMembership={(languageUseSchemeId, nextTaskIds) => {
              if (!examId) return Promise.resolve();

              // No optimistic patch here: a membership change cascades across an a-priori-unknown set
              // of other tasks (added tasks' own criteria cleared, removed tasks' own criteria created,
              // other groups losing members or disappearing) — correctly mirroring that client-side
              // would duplicate the mutation's own logic. Resync via refreshMsEditData instead; the
              // popover shows its own saving state while this is in flight.
              return updateLanguageUseSchemeMembershipMutation(examId, languageUseSchemeId, nextTaskIds)()
                .then(() => {
                  triggerExamUpdate();
                  return refreshMsEditData(false);
                })
                .catch(err => {
                  logger.error(err, `Error updating language-use group membership:`);
                  throw err;
                });
            }}
            onDeleteLanguageUseGroup={languageUseSchemeId => {
              if (!examId) return Promise.resolve();

              return deleteLanguageUseSchemeMutation(examId, languageUseSchemeId)()
                .then(() => {
                  triggerExamUpdate();
                  return refreshMsEditData(false);
                })
                .catch(err => {
                  logger.error(err, `Error deleting language-use scheme:`);
                  throw err;
                });
            }}
          />
        );
      case 'correction':
        if (enhancedSubmissions.length === 0 && !exam) {
          return (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4">
              <div className="w-8 h-8 border-4 border-border border-t-brand-strong rounded-full animate-spin" />
              <span className="text-sm font-medium">{t('Chat.loading.examData')}</span>
            </div>
          );
        }
        return (
          <CombinedTaskCorrection
            submissions={enhancedSubmissions}
            exam={exam}
            refiningGradingSchemeTaskIds={refiningGradingSchemeTaskIds}
            refiningGradingSchemeLanguageUseTaskIds={refiningGradingSchemeLanguageUseTaskIds}
            regradingContentKeys={regradingContentKeys}
            regradingLanguageUseKeys={regradingLanguageUseKeys}
            queuedContentKeys={queuedContentKeys}
            queuedLanguageUseKeys={queuedLanguageUseKeys}
            onSaveUpdate={handleSaveSubmissionTask}
            onDeleteGrammarError={handleDeleteGrammarError}
            onAddGrammarError={handleAddGrammarError}
            onUpdateGrammarError={handleUpdateGrammarError}
            initialSubmissionIdx={canvasState.data?.submissionIdx}
            initialTaskKey={canvasState.data?.taskKey}
            initialBoxMode={canvasState.data?.boxMode}
            onOpenCanvas={openCanvas}
            focusFirstLowConfidence={Boolean(canvasState.data?.focusFirstLowConfidence)}
            focusFirstLowConfidenceMode={canvasState.data?.focusFirstLowConfidenceMode}
            mode={canvasState.data?.mode ?? 'correction'}
            onHeaderContent={setCorrectionHeaderContent}
            onVisibleTaskChange={setVisibleCorrectionTask}
          />
        );
      case 'structureeditor':
        const extractionProgress =
          (exam as any)?.examState?.taskExtractionProgress || liveTaskExtractionProgress || null;
        const extractionStatus = String(extractionProgress?.status ?? '').toUpperCase();
        const extractionTotal = Number(extractionProgress?.total ?? 0);
        // EITHER SHAPE, because `extractionProgress` is either of two things: the exam's stored record, which
        // carries the SET of done units, or `liveTaskExtractionProgress`, which carries the count already taken
        // from it. Reading only `.finished` meant the stored record — which wins the `||` above and always
        // exists, because it is seeded — reported 0, so this label sat at "0 / N" for the whole extraction and
        // `extractionDone` could only ever be reached through the status.
        const extractionFinishedCount = Array.isArray(extractionProgress?.finishedUnits)
          ? extractionProgress.finishedUnits.length
          : Number(extractionProgress?.finished ?? 0);
        const extractionDone =
          extractionStatus === 'COMPLETED' || (extractionTotal > 0 && extractionFinishedCount >= extractionTotal);
        const showExtractionLoading = !extractionDone && (extractionStatus === 'IN_PROGRESS' || extractionTotal > 0);

        if (showExtractionLoading) {
          return (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4">
              <div className="w-8 h-8 border-4 border-border border-t-brand-strong rounded-full animate-spin" />
              <span className="text-sm font-medium">
                {t('Chat.loading.extractingTasks', {
                  finished: Math.max(0, extractionFinishedCount),
                  total: Math.max(0, extractionTotal),
                })}
              </span>
            </div>
          );
        }

        if (!exam) {
          return (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4">
              <div className="w-8 h-8 border-4 border-border border-t-brand-strong rounded-full animate-spin" />
              <span className="text-sm font-medium">{t('Chat.loading.examStructure')}</span>
            </div>
          );
        }

        const hasBlankImages = Array.isArray(exam?.blankExamIds) && exam.blankExamIds.length > 0;
        const isLoadingPages = hasBlankImages && pageUrls.length === 0;

        if (isLoadingPages) {
          return (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4">
              <div className="w-8 h-8 border-4 border-border border-t-brand-strong rounded-full animate-spin" />
              <span className="text-sm font-medium">{t('Chat.loading.pages')}</span>
            </div>
          );
        }

        return (
          <StructureEditor
            exam={exam}
            pages={pageUrls}
            isReadOnly={isStructureReadOnly}
            onSave={async (tasks, options) => {
              if (!examId) return;
              const boxIds = options?.rerunDescriptionOCRBoxIds ?? [];
              const blankExamIds = Array.isArray(exam?.blankExamIds) ? exam.blankExamIds : [];
              const canvasBoxesById = new Map<string, any>();
              for (const task of tasks) {
                for (const box of task.boundingBoxes || []) {
                  const boxId = String((box as any)?.id ?? '').trim();
                  if (!boxId) continue;
                  canvasBoxesById.set(boxId, box);
                }
              }

              const updatedBoxesForOcrRerun = boxIds
                .map(id => {
                  const box = canvasBoxesById.get(String(id));
                  if (!box) return null;
                  const x = Number((box as any).x);
                  const y = Number((box as any).y);
                  const width = Number((box as any).width);
                  const height = Number((box as any).height);
                  const pageNumber = Number((box as any).pageNumber ?? 0) + 1;
                  if (![x, y, width, height, pageNumber].every(v => Number.isFinite(v))) return null;

                  return {
                    id: String(id),
                    pageNumber: Math.max(1, Math.round(pageNumber)),
                    bbox: {
                      x1: x * 10,
                      y1: y * 10,
                      x2: (x + width) * 10,
                      y2: (y + height) * 10,
                    },
                  };
                })
                .filter(Boolean);

              const persistedBoxIds = new Set<string>();
              const collectPersistedBoxIds = (taskList: any[] | undefined) => {
                if (!Array.isArray(taskList)) return;
                for (const task of taskList) {
                  if (Array.isArray(task?.boundingBoxes)) {
                    for (const box of task.boundingBoxes) {
                      const id = String(box?._id ?? box?.id ?? '').trim();
                      if (id) persistedBoxIds.add(id);
                    }
                  }
                  if (Array.isArray(task?.subTasks)) {
                    collectPersistedBoxIds(task.subTasks);
                  }
                }
              };
              collectPersistedBoxIds(Array.isArray((exam as any)?.tasks) ? (exam as any).tasks : []);
              const hasUnsavedBoxIds = boxIds.some(id => !persistedBoxIds.has(String(id)));

              if (boxIds.length > 0 && !hasUnsavedBoxIds) {
                logger.info(`[StructureEditor Save] Using OCR-only mutation for boxIds: ${JSON.stringify(boxIds)}`);
                await rerunBoundingBoxOCRMutation(
                  examId,
                  boxIds,
                  blankExamIds,
                  JSON.stringify(updatedBoxesForOcrRerun),
                )();
              } else if (boxIds.length > 0) {
                logger.info(
                  `[StructureEditor Save] Using full structure save for unsaved boxIds: ${JSON.stringify(boxIds)}`,
                );
                await createExamTasksMutation(
                  examId,
                  JSON.stringify({ tasks: serializeStructureEditorTasks(tasks) }),
                  '',
                  blankExamIds,
                  boxIds,
                )();
              } else {
                logger.info(`[StructureEditor Save] Using full structure save (no boxIds)`);
                await createExamTasksMutation(
                  examId,
                  JSON.stringify({ tasks: serializeStructureEditorTasks(tasks) }),
                  '',
                  blankExamIds,
                  [],
                )();
              }
              await fetchExamData();
              triggerExamUpdate();
            }}
            onSaveAdditionalInfo={async additionalInfo => {
              if (!examId) return;
              const blankExamIds = Array.isArray(exam?.blankExamIds) ? exam.blankExamIds : [];
              if (!blankExamIds.length) return;
              const items = additionalInfo
                .flatMap(info =>
                  info.boundingBoxes.map(box => ({
                    id: info.id,
                    boundingBoxId: box.id,
                    imageId: box.imageId ?? null,
                    approved: box.approved ?? false,
                    x: box.x,
                    y: box.y,
                    width: box.width,
                    height: box.height,
                    pageNumber: box.pageNumber,
                    ocrExtractedText: box.ocrExtractedText || '',
                  })),
                )
                .filter(item => Boolean(item.boundingBoxId));
              await saveAdditionalInfoMutation(examId, blankExamIds, JSON.stringify(items))();
              await fetchExamData();
              triggerExamUpdate();
            }}
          />
        );
      case 'examcreator':
        return <ExamEditor exam={exam} isReadOnly={isExamCreatorReadOnly} role={role} />;
      case 'summary':
        if (enhancedSubmissions.length === 0 && !exam) {
          return (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4">
              <div className="w-8 h-8 border-4 border-border border-t-brand-strong rounded-full animate-spin" />
              <span className="text-sm font-medium">{t('Chat.loading.summary')}</span>
            </div>
          );
        }
        return (
          <CorrectionSummary
            submissions={enhancedSubmissions}
            exam={exam}
            onSaveUpdate={handleSaveSubmissionTask}
            onSaveGradingScale={async scale => {
              if (!exam?._id) return;
              const gradingScaleJson = JSON.stringify(scale);
              await updateGradingScaleMutation(exam._id, gradingScaleJson)();
              // Keep the in-memory exam in sync so the saved scale survives remounts
              // (e.g. navigating into a submission and back, or reopening the canvas).
              setExam(prev => {
                if (!prev) return prev;
                const prevData = typeof (prev as any).toObject === 'function' ? (prev as any).toObject() : prev;
                return cloneExamForState({ ...prevData, gradingScaleJson });
              });
            }}
            onSaveGrammarFiSettings={async settings => {
              if (!exam?._id) return;
              const grammarFiSettingsJson = JSON.stringify(settings);
              await updateGrammarFiSettingsMutation(exam._id, grammarFiSettingsJson)();
              // Keep the in-memory exam in sync so the saved settings survive remounts.
              setExam(prev => {
                if (!prev) return prev;
                const prevData = typeof (prev as any).toObject === 'function' ? (prev as any).toObject() : prev;
                return cloneExamForState({ ...prevData, grammarFiSettingsJson });
              });
            }}
            teacherAlwaysUseFi={teacherAlwaysUseFi}
            onDownloadReports={handleDownloadReports}
            isDownloadingReports={isDownloadingReports}
            canDownloadReports={canDownloadReports}
            downloadDisabledReason={reportDownloadBlockedReason ?? undefined}
            onDownloadGradebookCsv={handleDownloadGradebookCsv}
            isDownloadingGradebookCsv={isDownloadingCsv}
            canDownloadGradebookCsv={canDownloadReports}
            gradebookCsvDisabledReason={reportDownloadBlockedReason ?? undefined}
            onNavigateToTask={(submissionIdx, taskKey, mode, boxMode) => {
              setCanvasState({
                isOpen: true,
                view: 'correction',
                data: { submissionIdx, taskKey, mode: mode ?? 'correction', boxMode },
              });
            }}
          />
        );
      case 'submissioncheck':
        // A student only ever uploads their own single exam — nothing to review here. Safety net
        // for any trigger path that isn't already gated (see StepsIconSidebar/ChatInput above).
        if (role === 'student') return null;
        return (
          <SubmissionReview
            submissions={submissions as any}
            lockingEnabled={SUBMISSION_READONLY_ON_CONFIRM}
            onSave={handleSaveSubmissionReview}
            onCreateNewSubmission={handleCreateNewSubmission}
            onDeleteSubmission={handleDeleteSubmission}
          />
        );
      case 'demoselector':
        return (
          <DemoSelector
            loadingId={demoLoadingId}
            onSelectDemo={demoId => {
              const exam = SAMPLE_EXAMS.find(e => e.id === demoId);
              if (!exam || demoLoadingId) return;
              setDemoLoadingId(demoId);
              void (async () => {
                try {
                  const context = await ensureConversationAndExamContext();
                  if (!context) return;
                  setCanvasState({ isOpen: false, view: null });
                  const convoLang = (conversation as any)?.language ?? i18n.language ?? 'de';
                  const message = buildSampleExamMessage(exam.files, convoLang);
                  setPendingDemoMessage(message);
                  const attachedFileNames = await loadSampleExamFiles(exam.files, context.examId);
                  setPendingDemoMessage(null);
                  await handleSubmit(message, { attachedFileNames });
                } catch (err) {
                  logger.error(err, `Demo load failed:`);
                } finally {
                  setDemoLoadingId(null);
                  setPendingDemoMessage(null);
                }
              })();
            }}
          />
        );
      default:
        return (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
              <LayoutTemplate className="h-7 w-7 text-slate-400 dark:text-slate-500" />
            </div>
            <p className="font-medium text-slate-600 dark:text-slate-300">{t('Canvas.common.emptyTitle')}</p>
            <p className="max-w-xs text-sm text-slate-400 dark:text-slate-500">{t('Canvas.common.emptyDescription')}</p>
          </div>
        );
    }
  };

  const guestAuthButtons = showGuestAuthButtons ? (
    <div className="absolute top-4 right-6 z-20 flex items-center gap-2">
      <button
        onClick={onOpenLogin}
        className="px-5 py-1.5 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-accent transition-colors border border-border bg-background/80 backdrop-blur-sm"
      >
        {t('Chat.auth.login')}
      </button>
      <button
        onClick={onOpenSignup}
        className="px-5 py-1.5 rounded-full text-sm font-semibold bg-brand-strong text-white hover:bg-brand-700 transition-all shadow-sm hover:shadow-md"
      >
        {t('Chat.auth.register')}
      </button>
    </div>
  ) : null;

  const guestAuthButtonsWithDivider =
    showGuestAuthButtons && messages.length > 0 ? (
      <div className="absolute inset-x-0 top-0 z-20 bg-background/80 backdrop-blur-sm">
        <div className="flex justify-end px-6 py-3">
          <div className="flex gap-2">
            <button
              onClick={onOpenLogin}
              className="px-5 py-1.5 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-accent transition-colors border border-border bg-background"
            >
              {t('Chat.auth.login')}
            </button>
            <button
              onClick={onOpenSignup}
              className="px-5 py-1.5 rounded-full text-sm font-semibold bg-brand-strong text-white hover:bg-brand-700 transition-all shadow-sm hover:shadow-md"
            >
              {t('Chat.auth.register')}
            </button>
          </div>
        </div>
        <div className="border-b border-border" />
      </div>
    ) : null;

  if (messages.length === 0 && !isLoading && !canvasState.isOpen) {
    return (
      <div className="relative h-full w-full">
        {guestAuthButtons}
        <DashboardView
          onSubmit={handleSubmit}
          examId={examId}
          onEnsureExam={async () => {
            const context = await ensureConversationAndExamContext();
            return context?.examId ?? null;
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full overflow-hidden">
      <div
        className={cn(
          'relative flex flex-col h-full ease-in-out min-w-0',
          guestAuthButtonsWithDivider ? 'pt-[61px]' : '',
          canvasState.isOpen
            ? 'flex-1 border-r border-border bg-background'
            : 'w-full max-w-5xl mx-auto bg-background transition-all duration-300',
        )}
      >
        {guestAuthButtonsWithDivider ?? guestAuthButtons}
        <div
          className={cn(
            'flex flex-1 min-h-0 flex-col overflow-hidden p-2 sm:p-4',
            !canvasState.isOpen && examId && 'pr-20 sm:pr-20',
          )}
        >
          <ChatMessages
            messages={messages}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            isThinking={Boolean(conversation?.thinking)}
            conversationId={conversationId}
            conversationLanguage={(conversation as any)?.language ?? null}
            onEditMessage={handleEditMessage}
            examId={examId}
            onExtractionComplete={refetchConversation}
            onExamRefresh={fetchExamData}
            onOpenCanvas={view => openCanvas(view)}
            panelRefsRef={panelRefsRef}
            splitConfirmationState={(exam as any)?.examState?.splitConfirmationState ?? null}
            structureConfirmationState={(exam as any)?.examState?.structureConfirmationState ?? null}
            isAgentThinking={isAgentThinking}
            agentThinkingText={agentThinkingText}
            pendingMessage={pendingDemoMessage}
          />
        </div>

        <div
          className={cn('shrink-0 px-2 sm:px-4 pt-2 pb-0 sm:pb-2', !canvasState.isOpen && examId && 'pr-20 sm:pr-20')}
        >
          {error && (
            <div className="alert alert-error mb-2">
              <span>{t('Chat.alerts.errorOccurred')}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setError(null)}>
                ✕
              </button>
            </div>
          )}
          <ChatInput
            onSubmit={handleSubmit}
            isLoading={isInputLocked}
            isAgentThinking={isAgentThinking || Boolean(conversation?.thinking)}
            suggestions={currentSuggestions}
            suggestionsLoading={Boolean(conversation?.suggestionsDirty)}
            examId={examId}
            onUiRefreshRequested={handleUiRefreshRequested}
            answerBbxUncertaintyCount={answerBbxUncertaintyCount}
            correctionUncertaintyCount={correctionUncertaintyCount}
            role={role}
            onMessageClick={(category, data) => {
              if (category) openCanvas(category as any, data);
            }}
            onStepStatusChange={(statuses, percents, lowConfs, counts, hasExamCreator, hasStructure) => {
              setStepStatuses(statuses);
              setStepPercents(percents);
              setStepLowConfs(lowConfs);
              if (counts) setStepCounts(counts);
              setHasExamCreatorStep(Boolean(hasExamCreator));
              setHasStructureStep(Boolean(hasStructure));
            }}
          />
        </div>
      </div>

      {!canvasState.isOpen && examId && (
        <StepsIconSidebar
          onStepClick={openCanvas}
          stepStatuses={stepStatuses}
          stepPercents={stepPercents}
          stepLowConfs={stepLowConfs}
          stepCounts={stepCounts}
          showExamCreatorStep={hasExamCreatorStep}
          showStructureStep={hasStructureStep}
          hideUploadStep={Boolean((exam as any)?.examState?.onlineSubmissionReceived)}
          role={role}
        />
      )}

      {canvasState.isOpen && (
        <ChatCanvas
          title={
            canvasState.view === 'boundingbox'
              ? t('Chat.canvasTitles.boundingbox')
              : canvasState.view === 'msedit'
                ? t('Chat.canvasTitles.msedit')
                : canvasState.view === 'correction'
                  ? (correctionHeaderContent ?? t('Chat.canvasTitles.correction'))
                  : canvasState.view === 'summary'
                    ? t('Chat.canvasTitles.summary')
                    : canvasState.view === 'structureeditor'
                      ? t('Chat.canvasTitles.structureeditor')
                      : canvasState.view === 'submissioncheck'
                        ? t('Chat.canvasTitles.submissioncheck')
                        : canvasState.view === 'demoselector'
                          ? t('Chat.canvasTitles.demoselector')
                          : canvasState.view === 'examcreator'
                            ? t('Chat.canvasTitles.examcreator')
                            : t('Chat.canvasTitles.default')
          }
          initialWidth={
            canvasState.view === 'structureeditor'
              ? 650
              : canvasState.view === 'msedit'
                ? 600
                : canvasState.view === 'correction'
                  ? 575
                  : 500
          }
          minWidth={canvasState.view === 'structureeditor' ? 400 : canvasState.view === 'correction' ? 360 : undefined}
          onClose={() => {
            setCanvasState({ isOpen: false, view: null });
            setCorrectionHeaderContent(null);
          }}
          onOpenSummary={canvasState.view === 'correction' ? () => openCanvas('summary') : undefined}
        >
          {renderCanvasView()}
        </ChatCanvas>
      )}
    </div>
  );
}
